import { getUndoState } from '@/mcp/init-undo'
import { mcpBus, type StudioMcpBus } from '@/mcp/studio-mcp-bus'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { findElementInTree } from '@/features/page-editor/store/canvas-slice'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { resolveFlowOwner } from '@/features/flow-editor/services/flow-ownership'
import { buildOpenPencilCogoDesignMutationImpactPreview } from '@/features/openpencil-figma/openpencil-cogo-design-mutation'
import { readCogoDocumentWorkspaceFromProject } from '@/features/document-workspace/model/document-operation'
import { loadProject } from '@/services/project-api'
import { flushEditorFullPersist } from '@/store/auto-save-bridge'
import { flushSaves } from '@/store/auto-save'
import { useStudioStore } from '@/store/studio-store'
import { RADIUS_MAP, THEME_PRESETS, resolveRadiusScale } from '@/store/theme-slice'
import {
  getStudioChatOperationContract,
  listStudioChatOperationContracts,
} from './studio-chat-operation-registry'
import type { StudioJsonSchema } from './studio-operation-v2'
import {
  buildStudioOperationImpactPaths,
  getStudioOperationImpactContract,
} from './studio-operation-impact-contract'
import { buildStudioSemanticSyncPlan } from './studio-semantic-sync'
import {
  consumeStudioCapabilityApprovalGrant,
  issueStudioCapabilityApprovalGrant,
} from './studio-capability-approval-grant'
import {
  COOGO_EXECUTION_EVENT_V2_VERSION,
  COOGO_EXECUTION_CHECKPOINT_V1_VERSION,
  CoogoExecutionError,
  CoogoOrchestrationExecutor,
  validateCoogoExecutionEvent,
  validateCoogoOrchestrationPlan,
  type CoogoCheckpointObservationV1,
  type CoogoCheckpointExactImpactOutputV1,
  type CoogoExecutionCheckpointV1,
  type CoogoExecutionEvent,
  type CoogoExecutionResult,
  type CoogoExecutionSnapshot,
  type CoogoOrchestrationPlan,
  type CoogoOrchestrationPlanV2,
  type CoogoReadableExecutionEvent,
  type CoogoReadableOrchestrationPlan,
} from '@cogo/webmcp-runtime'
import {
  appendStudioOrchestrationEvent,
  compileStudioOrchestrationPlan,
  studioOrchestrationReference,
} from './studio-orchestration-plan'
import {
  appendStudioOrchestrationJournalEvent,
  cancelStudioOrchestrationJournal,
  createStudioOrchestrationJournal,
  listStudioOrchestrationJournals,
  readStudioOrchestrationJournal,
  type StudioOrchestrationJournalMode,
  type StudioOrchestrationRunReadback,
} from './studio-orchestration-journal-api'
import {
  containsStudioOrchestrationSensitiveData,
  sanitizeStudioOrchestrationValue as sanitize,
} from './studio-orchestration-sensitive-data'
import {
  classifyStudioExactImpactTarget,
  createStudioExactImpactReadbackTool,
} from './studio-exact-impact-readback'
import {
  classifyStudioCoogoExecutionRecovery,
  classifyStudioCoogoUnknownMutationOutcome,
  createStudioCoogoExecutionJournal,
  type StudioCoogoExecutionJournal,
  type StudioCoogoUnknownMutationOutcome,
} from './studio-coogo-execution-journal'
import { createStudioCoogoExecutionLeaseController } from './studio-coogo-execution-lease-controller'
import { createStudioOrchestrationNodeHandlers } from './studio-orchestration-node-handlers'
import {
  isKnownStudioExecutionErrorCode,
  type StudioExecutionErrorCode,
} from '@/i18n/studio-execution-copy'

const PENDING_TTL_MS = 10 * 60 * 1000
const MAX_PENDING = 20
const MAX_EXECUTIONS = 50
const MAX_CONFIRMATION_LIFECYCLES = 200
const MAX_AG_UI_OPERATION_REQUESTS = 200
const CAPABILITY_SESSION_STORAGE_KEY = 'cogo.studio.capability.session.v1'
const CAPABILITY_EXECUTION_STORAGE_PREFIX = 'cogo.studio.capability.executions.v2.'
const CAPABILITY_PENDING_STORAGE_PREFIX = 'cogo.studio.capability.pending.v1.'
const CAPABILITY_STEP5_REFERENCE_STORAGE_PREFIX = 'cogo.studio.capability.step5-ref.v1.'
const LEGACY_EXECUTION_STORAGE_KEY = 'cogo.studio.capability.executions.v1'
const CORE_SURFACE_READBACK_TOOL = 'cogo.studio.capability.readbackCoreSurface'
const HANDLER_ENFORCED_APPROVAL_TOOLS = new Set([
  'cogo.studio.apply_openpencil_cogo_design_mutation',
  'cogo.studio.import_figma_frame',
])
type StudioCoreSurface = 'page' | 'actionflow' | 'data' | 'document'

interface StudioCoreSurfaceObservation {
  targetId: string
  authority: Record<string, boolean>
  secondary: Record<string, unknown>
  secondaryVerified: boolean
  present: boolean
  verified: boolean
}

export interface StudioCapabilityCommand {
  tool: string
  params: Record<string, unknown>
  operationId?: string
  sourceCallId?: string
  expectedChangedPaths?: string[]
  exactChanges?: StudioCapabilityChangePreview[]
  requiresConfirmation?: boolean
  readback?: {
    tool: string
    params?: Record<string, unknown>
  }
}

export interface StudioCapabilityPlan {
  planId: string
  runId?: string
  parentRunId?: string
  label: string
  intent?: string
  source: 'chat-intent-chunk' | 'chat-operation' | 'webmcp' | 'studio-ui'
  commands: StudioCapabilityCommand[]
}

export interface StudioCapabilityStateReadback {
  capturedAt: string
  projectId: string | null
  activePageId: string | null
  pageCount: number
  blockCount: number
  flowCount: number
  sourceWorkspaceRevision: number | null
  documentWorkspaceRevision: number | null
  fingerprint: string
}

export interface StudioCapabilityChangePreview {
  path: string
  label: string
  before?: unknown
  proposed?: unknown
}

export interface StudioCapabilityImpactPreview {
  operationId: string | null
  affectedSurfaces?: Array<'app' | 'design' | 'guide' | 'insights' | 'release'>
  summary: string
  targetLabel: string | null
  risk: 'read' | 'reversible' | 'destructive' | 'external'
  confirmation: 'none' | 'user' | 'typed'
  confirmationPhrase?: string
  changes: StudioCapabilityChangePreview[]
  readback: string
  undo: string
  persistence: 'session' | 'memory-only'
  currentState: StudioCapabilityStateReadback
}

export type StudioCapabilityFailureCode =
  | 'CONFIRMATION_MISSING_OR_EXPIRED'
  | 'CONFIRMATION_SCOPE_MISMATCH'
  | 'CONFIRMATION_PHRASE_MISMATCH'
  | 'CONFIRMATION_STALE'
  // wave-3 (Lane A) — closes the post-confirmation write window measured at
  // docs/superpowers/specs/2026-08-17-studio-slice4-concurrent-write.md.
  // CONFIRMATION_STALE (above) is the single, one-time, pre-apply staleness
  // re-check; CONFIRMATION_STALE_AT_COMMIT is a SECOND re-check of the same
  // comparison, run as the last synchronous step before the writer's actual
  // mutation (inside the tool handler, past the bus's middleware dispatch —
  // see exactChangesDriftFromLiveState() below). Fires cell (b): a user edit
  // landing in the await gap between the pre-apply check and the write.
  | 'CONFIRMATION_STALE_AT_COMMIT'
  // wave-3 (Lane A) — fires cell (c): a second, independently-confirmed plan
  // (different confirmationId) whose declared document paths overlap a plan
  // that is currently applying. See documentPathsInFlight below —
  // confirmationsInFlight (next) only protects the SAME confirmationId from
  // re-entry, not two different plans racing the same document.
  | 'CONFIRMATION_DOCUMENT_LOCKED'
  | 'CONFIRMATION_IN_FLIGHT'
  | 'DURABLE_JOURNAL_REQUIRED'

// task-1 (studio-ko-quality-repair-wave-1) — widened from the historical
// 'execution_lease_lost' | 'mutation_outcome_unknown' pair to the full
// display-boundary enumeration (StudioExecutionErrorCode, the closed set of
// codes CoogoExecutionError is actually thrown with — see
// studio-execution-copy.ts). The narrower pair silently dropped every other
// code before it ever reached StudioCapabilityExecution.executionFailureCode
// (see executorError() below), so the Task Center / execution-timeline
// display boundary could only ever resolve locale copy for these two codes
// and fell back to raw execution.error text for the rest.
//
// 'unknown' (fix-round-1, I3/I4) is a designated fallback member — never
// produced by an `as` cast. A CoogoExecutionError.code that reaches this
// boundary but is NOT in StudioExecutionErrorCode (e.g. an executor-package
// code like 'sibling_failed', or CoogoExecutionError's own 'execution_failed'
// default) narrows to this member via narrowExecutionFailureCode() below,
// rather than being admitted verbatim into this closed-claiming type. The
// display-boundary resolver (resolveStudioExecutionErrorCopy) already
// treats 'unknown' the same as "no code at all" — falls back to
// errors.unknown — so this is a type-honesty fix, not a behavior change.
type StudioCapabilityExecutionFailureCode =
  | StudioExecutionErrorCode
  | 'mutation_outcome_unknown'
  | 'unknown'

/**
 * Validating narrow for a CoogoExecutionError.code reaching this
 * persistence/display boundary (fix-round-1, I3). CoogoExecutionError.code
 * is `readonly code: string` (default 'execution_failed' —
 * packages/cogo-webmcp-runtime/src/orchestration/executor.ts:944,947) and
 * may also carry a server-supplied value
 * (StudioOrchestrationJournalRequestError); casting it directly with `as`
 * would admit an arbitrary string into StudioCapabilityExecutionFailureCode.
 * A recognized code narrows to itself; anything else narrows to the
 * designated 'unknown' fallback member.
 */
function narrowExecutionFailureCode(
  code: string,
): StudioCapabilityExecutionFailureCode {
  if (code === 'mutation_outcome_unknown') return code
  if (isKnownStudioExecutionErrorCode(code)) return code
  return 'unknown'
}

export type StudioCapabilityMutationRecoveryGuidance =
  | 'fresh-fence-and-durable-receipt-required-before-replay'
  | 'original-durable-receipt-required-before-completion'
  | 'manual-reconciliation-required'

export type StudioCapabilityMutationRecoveryReceipt =
  StudioCoogoUnknownMutationOutcome & {
    schemaVersion: 'studio.mutation-recovery.v1'
    guidance: StudioCapabilityMutationRecoveryGuidance
  }

export interface StudioCapabilityOrchestrationReference {
  planSchemaVersion: 'coogo.orchestration-plan.v1' | 'coogo.orchestration-plan.v2'
  eventSchemaVersion: 'coogo.execution-event.v1' | 'coogo.execution-event.v2'
  runId: string
  parentRunId?: string
  nodeIds: string[]
}

export interface StudioCapabilityConfirmationLifecycle {
  confirmationId: string
  status: 'pending' | 'executing' | 'executed' | 'cancelled' | 'failed'
  failureCode?: StudioCapabilityFailureCode
  error?: string
  executionId?: string
}

export interface StudioCapabilityExecution {
  executionId: string
  planId: string
  label: string
  source: StudioCapabilityPlan['source']
  projectId: string | null
  sessionId: string
  status: 'executed' | 'failed' | 'undone' | 'running' | 'suspended' | 'cancelled'
  tools: string[]
  changedPaths: string[]
  before: StudioCapabilityStateReadback
  after: StudioCapabilityStateReadback
  results: Array<{
    tool: string
    success: boolean
    data?: unknown
    error?: string
    readback?: { status: 'success'; data?: unknown; retained?: boolean } | { status: 'failed'; error: string }
  }>
  undoAvailable: boolean
  rollback?: {
    attempted: boolean
    succeeded: boolean
    error?: string
  }
  approval: {
    required: boolean
    source: 'not-required' | 'awaiting-user-approval' | 'not-approved' | 'studio-user-action' | 'webmcp-user-approval'
    confirmationId?: string
    approvedAt?: string
  }
  createdAt: string
  error?: string
  executionFailureCode?: StudioCapabilityExecutionFailureCode
  recovery?: StudioCapabilityMutationRecoveryReceipt
  orchestration: StudioCapabilityOrchestrationReference
  events: CoogoReadableExecutionEvent[]
}

export type StudioCapabilityGatewayResult =
  | {
      status: 'confirmation-required'
      confirmationId: string
      planId: string
      label: string
      tools: string[]
      changedPaths: string[]
      projectId: string | null
      expiresAt: string
      impact: StudioCapabilityImpactPreview
      orchestration: StudioCapabilityOrchestrationReference
      lifecycle?: StudioCapabilityLifecycleProjection
    }
  | {
      status: 'executed'
      execution: StudioCapabilityExecution
    }
  | {
      status: 'failed'
      execution?: StudioCapabilityExecution
      code?: StudioCapabilityFailureCode
      executionFailureCode?: StudioCapabilityExecutionFailureCode
      recovery?: StudioCapabilityMutationRecoveryReceipt
      error: string
    }

type StudioCapabilityExecutionResult = Exclude<
  StudioCapabilityGatewayResult,
  { status: 'confirmation-required' }
>

interface PendingCapabilityPlan {
  confirmationId: string
  plan: StudioCapabilityPlan
  scope: StudioCapabilityScope
  createdAt: number
  expiresAt: number
  impact: StudioCapabilityImpactPreview
  orchestrationPlan: CoogoReadableOrchestrationPlan
  events: CoogoReadableExecutionEvent[]
  journalMode: StudioOrchestrationJournalMode
  lifecycleOwner?: 'executor-v2'
}

interface StoredStep5DurableReference {
  confirmationId: string
  runId: string
  projectId: string
  scopeKey: string
  createdAt: number
  expiresAt: number
}

export interface StudioCapabilityLifecycleProjection {
  runId: string
  nodeId: string
  status:
    | 'suspended'
    | 'running'
    | 'executed'
    | 'failed'
    | 'cancelled'
    | 'recovery-required'
}

interface StudioCapabilityScope {
  key: string
  projectId: string | null
  sessionId: string
  principalId: string
}

export interface StudioPendingCapability {
  confirmationId: string
  planId: string
  label: string
  tools: string[]
  changedPaths: string[]
  projectId: string | null
  createdAt: string
  expiresAt: string
  impact: StudioCapabilityImpactPreview
  orchestration: StudioCapabilityOrchestrationReference
}

type StudioCapabilityApprovalSource = Extract<
  StudioCapabilityExecution['approval']['source'],
  'studio-user-action' | 'webmcp-user-approval'
>

const pending = new Map<string, PendingCapabilityPlan>()
const confirmationsInFlight = new Set<string>()
// wave-3 (Lane A) — keys the in-flight exclusion by DOCUMENT PATH rather
// than confirmationId, closing the agent-vs-agent clobber window measured
// at docs/superpowers/specs/2026-08-17-studio-slice4-concurrent-write.md
// cell (c). Maps a document path currently being applied-to -> the
// confirmationId holding it, so a second, differently-confirmationId'd plan
// racing on the SAME path fails closed (CONFIRMATION_DOCUMENT_LOCKED)
// instead of silently clobbering the first plan's write. Held for the
// duration of confirmStudioCapabilityWithSource's try block (registered
// alongside confirmationsInFlight, released in the same finally).
const documentPathsInFlight = new Map<string, string>()
const durableHydratedScopes = new Set<string>()
const executionsByScope = new Map<string, StudioCapabilityExecution[]>()
const restoredPendingScopes = new Set<string>()
const agUiOperationRequests = new Map<
  string,
  {
    fingerprint: string
    promise?: Promise<StudioCapabilityGatewayResult>
    settled: boolean
  }
>()
const gatewayListeners = new Set<() => void>()
const confirmationLifecycles = new Map<
  string,
  StudioCapabilityConfirmationLifecycle & { scopeKey: string }
>()
const activeStep5Executors = new Map<string, CoogoOrchestrationExecutor>()
const step5DurableReferences = new Map<string, StoredStep5DurableReference>()
const step5PendingHydrations = new Map<string, Promise<void>>()
let inMemorySessionId: string | null = null
let legacyExecutionStorageCleared = false
let gatewayRevision = 0

function notifyGatewayListeners(): void {
  gatewayRevision += 1
  for (const listener of gatewayListeners) listener()
}

export function subscribeStudioCapabilityGateway(listener: () => void): () => void {
  gatewayListeners.add(listener)
  return () => gatewayListeners.delete(listener)
}

export function getStudioCapabilityGatewayRevision(): number {
  return gatewayRevision
}

function recordConfirmationLifecycle(
  scope: StudioCapabilityScope,
  lifecycle: StudioCapabilityConfirmationLifecycle,
): void {
  confirmationLifecycles.delete(lifecycle.confirmationId)
  confirmationLifecycles.set(lifecycle.confirmationId, {
    ...lifecycle,
    scopeKey: scope.key,
  })
  while (confirmationLifecycles.size > MAX_CONFIRMATION_LIFECYCLES) {
    const oldest = confirmationLifecycles.keys().next().value
    if (typeof oldest !== 'string') break
    confirmationLifecycles.delete(oldest)
  }
}

export function getStudioCapabilityConfirmationLifecycle(
  confirmationId: string,
): StudioCapabilityConfirmationLifecycle | null {
  const lifecycle = confirmationLifecycles.get(confirmationId)
  if (!lifecycle || lifecycle.scopeKey !== captureStudioCapabilityScope().key) return null
  const { scopeKey: _scopeKey, ...readback } = lifecycle
  return structuredClone(readback)
}

function stableHash(value: string): string {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function getSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

function clearLegacyExecutionStorage(): void {
  if (legacyExecutionStorageCleared) return
  legacyExecutionStorageCleared = true
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(LEGACY_EXECUTION_STORAGE_KEY)
    }
  } catch {
    // Legacy evidence may be unavailable in privacy-restricted browsers.
  }
}

function getCapabilitySessionId(): string {
  if (inMemorySessionId) return inMemorySessionId
  clearLegacyExecutionStorage()
  const storage = getSessionStorage()
  let stored: string | null = null
  try {
    stored = storage?.getItem(CAPABILITY_SESSION_STORAGE_KEY) ?? null
  } catch {
    // Fall back to a new in-memory session below.
  }
  const sessionId = stored || crypto.randomUUID()
  inMemorySessionId = sessionId
  try {
    storage?.setItem(CAPABILITY_SESSION_STORAGE_KEY, sessionId)
  } catch {
    // The in-memory session still isolates capability state.
  }
  return sessionId
}

function captureStudioCapabilityScope(): StudioCapabilityScope {
  const studio = useStudioStore.getState()
  const projectId = studio.currentProjectId
  const principalId = studio.user?.id ?? 'anonymous'
  const sessionId = getCapabilitySessionId()
  return {
    projectId,
    sessionId,
    principalId,
    key: JSON.stringify([sessionId, principalId, projectId]),
  }
}

function sameScope(left: StudioCapabilityScope, right: StudioCapabilityScope): boolean {
  return (
    left.sessionId === right.sessionId &&
    left.principalId === right.principalId &&
    left.projectId === right.projectId
  )
}

function executionStorageKey(scope: StudioCapabilityScope): string {
  return `${CAPABILITY_EXECUTION_STORAGE_PREFIX}${encodeURIComponent(scope.key)}`
}

function pendingStorageKey(scope: StudioCapabilityScope): string {
  return `${CAPABILITY_PENDING_STORAGE_PREFIX}${encodeURIComponent(scope.key)}`
}

function step5ReferenceStorageKey(scope: StudioCapabilityScope): string {
  return `${CAPABILITY_STEP5_REFERENCE_STORAGE_PREFIX}${encodeURIComponent(scope.key)}`
}

function isStoredStep5DurableReference(
  value: unknown,
  scope: StudioCapabilityScope,
): value is StoredStep5DurableReference {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const reference = value as Partial<StoredStep5DurableReference>
  return (
    typeof reference.confirmationId === 'string' &&
    typeof reference.runId === 'string' &&
    reference.projectId === scope.projectId &&
    reference.scopeKey === scope.key &&
    typeof reference.createdAt === 'number' &&
    typeof reference.expiresAt === 'number' &&
    reference.expiresAt > Date.now()
  )
}

function readStoredStep5DurableReferences(
  scope: StudioCapabilityScope,
): StoredStep5DurableReference[] | null {
  try {
    const raw = getSessionStorage()?.getItem(step5ReferenceStorageKey(scope))
    const parsed = raw ? (JSON.parse(raw) as unknown) : []
    if (!Array.isArray(parsed)) return []
    return parsed.filter((entry) => isStoredStep5DurableReference(entry, scope))
  } catch {
    // Durable Platform readback remains authoritative when storage is blocked.
    return null
  }
}

function restoreStep5DurableReferences(scope: StudioCapabilityScope): void {
  const restored = readStoredStep5DurableReferences(scope)
  if (restored === null) return
  for (const [confirmationId, reference] of step5DurableReferences) {
    if (reference.scopeKey === scope.key) step5DurableReferences.delete(confirmationId)
  }
  for (const reference of restored) {
    step5DurableReferences.set(reference.confirmationId, reference)
  }
}

function persistStep5DurableReferences(scope: StudioCapabilityScope): void {
  const references = [...step5DurableReferences.values()].filter(
    (reference) => reference.scopeKey === scope.key && reference.expiresAt > Date.now(),
  )
  try {
    const storage = getSessionStorage()
    if (references.length === 0) {
      storage?.removeItem(step5ReferenceStorageKey(scope))
      return
    }
    storage?.setItem(step5ReferenceStorageKey(scope), JSON.stringify(references))
  } catch {
    // The current in-memory projection remains usable without storage.
  }
}

function canPersistPlan(plan: StudioCapabilityPlan): boolean {
  return !containsStudioOrchestrationSensitiveData(plan.commands)
}

function isStoredPendingCapability(value: unknown, scope: StudioCapabilityScope): value is PendingCapabilityPlan {
  if (!value || typeof value !== 'object') return false
  const entry = value as Partial<PendingCapabilityPlan>
  return (
    typeof entry.confirmationId === 'string' &&
    typeof entry.createdAt === 'number' &&
    typeof entry.expiresAt === 'number' &&
    entry.expiresAt > Date.now() &&
    Boolean(entry.plan) &&
    Array.isArray(entry.plan?.commands) &&
    Boolean(entry.scope) &&
    entry.scope?.key === scope.key &&
    entry.scope?.projectId === scope.projectId &&
    entry.scope?.sessionId === scope.sessionId &&
    entry.scope?.principalId === scope.principalId &&
    Boolean(entry.impact) &&
    entry.impact?.persistence === 'session' &&
    Boolean(entry.orchestrationPlan) &&
    Array.isArray(entry.events) &&
    (entry.journalMode === 'durable' || entry.journalMode === 'session-only') &&
    validateCoogoOrchestrationPlan(entry.orchestrationPlan as CoogoOrchestrationPlan).valid &&
    entry.orchestrationPlan?.nodes.length === entry.plan?.commands.length &&
    entry.events.every((event) =>
      validateCoogoExecutionEvent(event as CoogoExecutionEvent).valid,
    ) &&
    canPersistPlan(entry.plan)
  )
}

function restorePendingCapabilities(scope: StudioCapabilityScope): void {
  if (restoredPendingScopes.has(scope.key)) return
  restoredPendingScopes.add(scope.key)
  try {
    const raw = getSessionStorage()?.getItem(pendingStorageKey(scope))
    const parsed = raw ? (JSON.parse(raw) as unknown) : []
    if (!Array.isArray(parsed)) return
    for (const entry of parsed
      .filter((item) => isStoredPendingCapability(item, scope))
      .slice(-MAX_PENDING)) {
      pending.set(entry.confirmationId, entry)
    }
  } catch {
    // Pending confirmations remain unavailable when browser storage is blocked.
  }
}

function persistPendingCapabilities(scope: StudioCapabilityScope): void {
  const entries = [...pending.values()].filter(
    (entry) =>
      sameScope(entry.scope, scope) &&
      entry.expiresAt > Date.now() &&
      entry.impact.persistence === 'session' &&
      canPersistPlan(entry.plan),
  )
  try {
    const storage = getSessionStorage()
    if (entries.length === 0) {
      storage?.removeItem(pendingStorageKey(scope))
      return
    }
    storage?.setItem(
      pendingStorageKey(scope),
      JSON.stringify(entries.map((entry) => sanitize(entry))),
    )
  } catch {
    // In-memory confirmation remains available for the current page lifetime.
  }
}

function isStoredMutationRecoveryReceipt(
  value: unknown,
): value is StudioCapabilityMutationRecoveryReceipt {
  const receipt = recordOf(value)
  if (!receipt) return false
  const expectedKeys = [
    'beforeValue',
    'guidance',
    'kind',
    'nodeId',
    'observedOutcome',
    'proposedValue',
    'recovery',
    'schemaVersion',
  ].sort()
  if (
    JSON.stringify(Object.keys(receipt).sort()) !== JSON.stringify(expectedKeys) ||
    receipt.schemaVersion !== 'studio.mutation-recovery.v1' ||
    receipt.kind !== 'mutation_outcome_unknown' ||
    receipt.nodeId !== 'n4-update-page-meta' ||
    receipt.recovery !== 'manual-reconciliation' ||
    typeof receipt.beforeValue !== 'string' ||
    typeof receipt.proposedValue !== 'string'
  ) {
    return false
  }
  return (
    (receipt.observedOutcome === 'exact-before' &&
      receipt.guidance === 'fresh-fence-and-durable-receipt-required-before-replay') ||
    (receipt.observedOutcome === 'exact-proposed' &&
      receipt.guidance === 'original-durable-receipt-required-before-completion') ||
    (receipt.observedOutcome === 'diverged' &&
      receipt.guidance === 'manual-reconciliation-required')
  )
}

function isStoredExecution(
  value: unknown,
  scope: StudioCapabilityScope,
): value is StudioCapabilityExecution {
  if (!value || typeof value !== 'object') return false
  const entry = value as Partial<StudioCapabilityExecution>
  return (
    typeof entry.executionId === 'string' &&
    typeof entry.planId === 'string' &&
    entry.projectId === scope.projectId &&
    entry.sessionId === scope.sessionId &&
    (entry.status === 'executed' ||
      entry.status === 'failed' ||
      entry.status === 'undone' ||
      entry.status === 'running' ||
      entry.status === 'suspended' ||
      entry.status === 'cancelled') &&
    Array.isArray(entry.tools) &&
    Array.isArray(entry.results) &&
    Boolean(entry.before) &&
    Boolean(entry.after) &&
    Boolean(entry.approval) &&
    Boolean(entry.orchestration) &&
    // fix-round-1 (I3) — sessionStorage is untrusted at this boundary
    // (arbitrary/corrupted/pre-migration JSON). Validate executionFailureCode
    // against the same closed set the display boundary resolves against,
    // rather than trusting the `as Partial<StudioCapabilityExecution>` cast
    // above; an unrecognized value fails the whole entry closed, same as an
    // unrecognized `status` does above.
    (entry.executionFailureCode === undefined ||
      entry.executionFailureCode === 'mutation_outcome_unknown' ||
      entry.executionFailureCode === 'unknown' ||
      isKnownStudioExecutionErrorCode(entry.executionFailureCode)) &&
    (entry.executionFailureCode === 'mutation_outcome_unknown'
      ? isStoredMutationRecoveryReceipt(entry.recovery)
      : entry.recovery === undefined) &&
    Array.isArray(entry.events) &&
    entry.events.every((event) =>
      validateCoogoExecutionEvent(event as CoogoExecutionEvent).valid,
    )
  )
}

function getScopedExecutions(scope: StudioCapabilityScope): StudioCapabilityExecution[] {
  const cached = executionsByScope.get(scope.key)
  if (cached) return cached

  let restored: StudioCapabilityExecution[] = []
  try {
    const raw = getSessionStorage()?.getItem(executionStorageKey(scope))
    const parsed = raw ? (JSON.parse(raw) as unknown) : []
    if (Array.isArray(parsed)) {
      restored = parsed.filter((entry) => isStoredExecution(entry, scope)).slice(-MAX_EXECUTIONS)
    }
  } catch {
    restored = []
  }
  executionsByScope.set(scope.key, restored)
  return restored
}

function persistExecutions(
  scope: StudioCapabilityScope,
  executions: StudioCapabilityExecution[],
): void {
  try {
    getSessionStorage()?.setItem(
      executionStorageKey(scope),
      JSON.stringify(executions.map((entry) => sanitize(entry))),
    )
  } catch {
    // Execution readback remains in memory when browser storage is unavailable.
  }
}

function documentRevision(project: unknown): number | null {
  if (!project || typeof project !== 'object') return null
  const workspace = (project as Record<string, unknown>).cogoDocumentWorkspace
  if (!workspace || typeof workspace !== 'object') return null
  const revision = (workspace as Record<string, unknown>).revision
  return typeof revision === 'number' ? revision : null
}

export function captureStudioCapabilityState(): StudioCapabilityStateReadback {
  const studio = useStudioStore.getState()
  const editor = useEditorStore.getState()
  const flow = useFlowEditorStore.getState()
  const pageSummary = (editor.app?.pages ?? []).map((page) => ({
    id: page.id,
    title: page.title,
    route: page.route,
    blocks: page.blocks,
  }))
  const summary = {
    projectId: studio.currentProjectId,
    activePageId: editor.activePageId,
    pages: pageSummary,
    blocks: Object.keys(editor.blocks).sort(),
    flows: flow.flows.map((item) => item.id).sort(),
    sourceWorkspaceRevision: studio.openPencilSourceWorkspace?.revision ?? null,
    documentWorkspaceRevision: documentRevision(studio.currentProject),
  }
  return {
    capturedAt: new Date().toISOString(),
    projectId: studio.currentProjectId,
    activePageId: editor.activePageId,
    pageCount: pageSummary.length,
    blockCount: Object.keys(editor.blocks).length,
    flowCount: flow.flows.length,
    sourceWorkspaceRevision: summary.sourceWorkspaceRevision,
    documentWorkspaceRevision: summary.documentWorkspaceRevision,
    fingerprint: stableHash(JSON.stringify(summary)),
  }
}

function operationIdFromPlan(plan: StudioCapabilityPlan): string | null {
  const match = /^operation:([^:]+):/.exec(plan.planId)
  return match?.[1] ?? null
}

function findNestedProperty(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object') return undefined
  if (!Array.isArray(value) && key in value) {
    return (value as Record<string, unknown>)[key]
  }
  for (const entry of Array.isArray(value)
    ? value
    : Object.values(value as Record<string, unknown>)) {
    const found = findNestedProperty(entry, key)
    if (found !== undefined) return found
  }
  return undefined
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function flowNameFromId(flowId: string): string {
  return (
    useFlowEditorStore.getState().flows.find((flow) => flow.id === flowId)?.name ??
    flowId.split(':').at(-1) ??
    flowId
  )
}

function projectFlowSnapshot(
  project: unknown,
  flowName: string,
): { blockName: string | null; flowKey: string; definition: unknown } | null {
  const projectRecord = recordOf(project)
  if (!projectRecord) return null
  const blocks = recordOf(projectRecord.blocks) ?? {}
  const resolved = resolveFlowOwner(
    blocks as Record<string, { flow?: Record<string, unknown>; events?: Record<string, unknown> }>,
    flowName,
  )
  if (resolved?.flow) {
    return {
      blockName: resolved.blockName,
      flowKey: resolved.flowKey,
      definition: structuredClone(resolved.flow),
    }
  }
  // Malformed hand-authored projects sometimes keep a declaration under a
  // non-canonical key whose definition still has the canonical name. Retain
  // detection, but never reorder blocks around a caller-supplied owner: the
  // first declaration remains the same authority used by resolveFlowOwner.
  for (const [blockName, block] of Object.entries(blocks)) {
    const flowContainer = recordOf(recordOf(block)?.flow)
    if (!flowContainer) continue
    const definitions = recordOf(flowContainer.flows) ?? flowContainer
    const match = Object.entries(definitions).find(
      ([flowKey, definition]) => flowKey === flowName || recordOf(definition)?.name === flowName,
    )
    if (match) {
      return {
        blockName,
        flowKey: match[0],
        definition: structuredClone(match[1]),
      }
    }
  }
  const mirror = recordOf(projectRecord.flows)
  const mirrorMatch = mirror
    ? Object.entries(mirror).find(
        ([flowKey, definition]) => flowKey === flowName || recordOf(definition)?.name === flowName,
      )
    : undefined
  if (mirrorMatch) {
    return {
      blockName: null,
      flowKey: mirrorMatch[0],
      definition: structuredClone(mirrorMatch[1]),
    }
  }
  return null
}

interface FlowReferenceSnapshot {
  path: string
  value: unknown
  authority: 'project' | 'editor'
}

function collectBindingValueReferences(
  value: unknown,
  names: ReadonlySet<string>,
  path: string,
  authority: FlowReferenceSnapshot['authority'],
): FlowReferenceSnapshot[] {
  if (typeof value === 'string') {
    return names.has(value) ? [{ path, value, authority }] : []
  }
  const binding = recordOf(value)
  if (!binding) return []
  const matches = ['flow', 'action', 'flowId', 'flowRef', 'flowName'].some((key) => {
    const candidate = binding[key]
    return typeof candidate === 'string' && names.has(candidate)
  })
  return matches ? [{ path, value: structuredClone(value), authority }] : []
}

function collectBlockEventReferences(
  events: unknown,
  names: ReadonlySet<string>,
  path: string,
  authority: FlowReferenceSnapshot['authority'],
): FlowReferenceSnapshot[] {
  const eventRecord = recordOf(events)
  if (!eventRecord) return []
  const references: FlowReferenceSnapshot[] = []
  const collectEntries = (
    entries: Record<string, unknown>,
    entryPath: string,
    skipBindingsWrapper: boolean,
  ): void => {
    for (const [key, value] of Object.entries(entries)) {
      if (skipBindingsWrapper && key === 'bindings') continue
      const valuePath = `${entryPath}/${encodeURIComponent(key)}`
      const direct = collectBindingValueReferences(value, names, valuePath, authority)
      if (direct.length > 0) {
        references.push(...direct)
        continue
      }
      const nested = recordOf(value)
      if (!nested || 'flow' in nested || 'action' in nested) continue
      const nestedLooksLikeEventMap = Object.values(nested).some(
        (entry) => collectBindingValueReferences(entry, names, valuePath, authority).length > 0,
      )
      if (!nestedLooksLikeEventMap) continue
      for (const [eventType, binding] of Object.entries(nested)) {
        references.push(
          ...collectBindingValueReferences(
            binding,
            names,
            `${valuePath}/${encodeURIComponent(eventType)}`,
            authority,
          ),
        )
      }
    }
  }
  const wrapper = recordOf(eventRecord.bindings)
  if (wrapper) {
    collectEntries(wrapper, `${path}/bindings`, false)
  }
  collectEntries(eventRecord, path, Boolean(wrapper))
  return references
}

function collectElementEventReferences(
  value: unknown,
  names: ReadonlySet<string>,
  path: string,
  authority: FlowReferenceSnapshot['authority'],
): FlowReferenceSnapshot[] {
  const node = recordOf(value)
  if (!node) return []
  const references: FlowReferenceSnapshot[] = []
  const events = recordOf(node.events)
  if (events) {
    for (const [eventType, binding] of Object.entries(events)) {
      references.push(
        ...collectBindingValueReferences(
          binding,
          names,
          `${path}/events/${encodeURIComponent(eventType)}`,
          authority,
        ),
      )
    }
  }
  for (const [key, child] of Object.entries(node)) {
    if (key === 'events') continue
    if (Array.isArray(child)) {
      child.forEach((entry, index) => {
        references.push(
          ...collectElementEventReferences(
            entry,
            names,
            `${path}/${encodeURIComponent(key)}/${index}`,
            authority,
          ),
        )
      })
    } else if (key === 'root') {
      references.push(...collectElementEventReferences(child, names, `${path}/root`, authority))
    }
  }
  return references
}

function collectFlowReferencesFromBlocks(
  blocks: Record<string, unknown>,
  names: ReadonlySet<string>,
  authority: FlowReferenceSnapshot['authority'],
): FlowReferenceSnapshot[] {
  const prefix = authority === 'project' ? '/blocks' : '/editor/blocks'
  const references: FlowReferenceSnapshot[] = []
  for (const [blockName, block] of Object.entries(blocks)) {
    const blockRecord = recordOf(block)
    if (!blockRecord) continue
    const blockPath = `${prefix}/${encodeURIComponent(blockName)}`
    references.push(
      ...collectBlockEventReferences(blockRecord.events, names, `${blockPath}/events`, authority),
      ...collectElementEventReferences(blockRecord.ui, names, `${blockPath}/ui`, authority),
    )
  }
  return references
}

function collectFlowReferences(project: unknown, names: Set<string>): FlowReferenceSnapshot[] {
  const projectBlocks = recordOf(recordOf(project)?.blocks) ?? {}
  const editorBlocks = useEditorStore.getState().blocks as Record<string, unknown>
  return [
    ...collectFlowReferencesFromBlocks(projectBlocks, names, 'project'),
    ...collectFlowReferencesFromBlocks(editorBlocks ?? {}, names, 'editor'),
  ].sort((left, right) => left.path.localeCompare(right.path))
}

function flowMirrorSnapshots(
  mirror: Record<string, unknown>,
  names: ReadonlySet<string>,
): Array<{ key: string; definition: unknown }> {
  return Object.entries(mirror)
    .filter(
      ([key, definition]) =>
        names.has(key) ||
        (typeof recordOf(definition)?.name === 'string' &&
          names.has(recordOf(definition)?.name as string)),
    )
    .map(([key, definition]) => ({
      key,
      definition: structuredClone(definition),
    }))
}

function projectFlowMirrorSnapshots(
  project: unknown,
  names: ReadonlySet<string>,
): Array<{ key: string; definition: unknown }> {
  return flowMirrorSnapshots(recordOf(recordOf(project)?.flows) ?? {}, names)
}

function editorFlowMirrorSnapshots(
  names: ReadonlySet<string>,
): Array<{ key: string; definition: unknown }> {
  return flowMirrorSnapshots(recordOf(useEditorStore.getState().flows) ?? {}, names)
}

function projectPageSnapshot(project: unknown, pageId: string): unknown {
  const manifest = recordOf(recordOf(project)?.manifest)
  const pages = Array.isArray(manifest?.pages) ? manifest.pages : []
  return pages.find((page) => recordOf(page)?.id === pageId)
}

function projectDatasourceSnapshot(
  project: unknown,
  blockName: string,
  sourceName: string,
): unknown {
  const block = recordOf(recordOf(recordOf(project)?.blocks)?.[blockName])
  const data = recordOf(block?.data) ?? recordOf(block?.datasource)
  return data && Object.prototype.hasOwnProperty.call(data, sourceName)
    ? data[sourceName]
    : undefined
}

function verifyCoreSurfaceObservation(
  authority: Record<string, boolean>,
  expectation: 'present' | 'absent',
  secondaryVerified: boolean,
): boolean {
  return (
    (expectation === 'absent'
      ? Object.values(authority).every((value) => !value)
      : Object.values(authority).every(Boolean)) && secondaryVerified
  )
}

function observeAuthoritativeCoreSurface(
  project: unknown,
  surface: StudioCoreSurface,
  expectation: 'present' | 'absent',
  payload: Record<string, unknown>,
): StudioCoreSurfaceObservation {
  let targetId = ''
  let authority: Record<string, boolean>
  let secondary: Record<string, unknown> = {}
  let secondaryVerified = true

  if (surface === 'page') {
    targetId = typeof payload.pageId === 'string' ? payload.pageId.trim() : ''
    const manifest = recordOf(recordOf(project)?.manifest)
    const pages = Array.isArray(manifest?.pages) ? manifest.pages : []
    const actualProjectPageIds = pages
      .map((page) => recordOf(page)?.id)
      .filter((value): value is string => typeof value === 'string')
    const projectDefaultRoute = recordOf(manifest?.navigation)?.defaultRoute ?? null
    const expectedProjectDefaultRoute =
      typeof payload.expectedProjectDefaultRoute === 'string' &&
      payload.expectedProjectDefaultRoute.length > 0
        ? payload.expectedProjectDefaultRoute
        : null
    const expectedProjectPageIds = Array.isArray(payload.expectedProjectPageIds)
      ? payload.expectedProjectPageIds.filter((value): value is string => typeof value === 'string')
      : null
    authority = {
      project: projectPageSnapshot(project, targetId) !== undefined,
    }
    secondary = {
      expectedProjectDefaultRoute,
      projectDefaultRoute,
      expectedProjectPageIds,
      actualProjectPageIds,
    }
    secondaryVerified =
      (expectedProjectDefaultRoute === null ||
        projectDefaultRoute === expectedProjectDefaultRoute) &&
      (expectedProjectPageIds === null ||
        JSON.stringify(actualProjectPageIds) === JSON.stringify(expectedProjectPageIds))
  } else if (surface === 'actionflow') {
    targetId = typeof payload.flowId === 'string' ? payload.flowId.trim() : ''
    const flowName =
      (typeof payload.flowName === 'string' && payload.flowName.trim()) || flowNameFromId(targetId)
    const flowKey = (typeof payload.flowKey === 'string' && payload.flowKey.trim()) || flowName
    const names = new Set([flowName, flowKey])
    const projectMirror = projectFlowMirrorSnapshots(project, names)
    const projectBlocks = recordOf(recordOf(project)?.blocks) ?? {}
    const projectBindings = collectFlowReferencesFromBlocks(projectBlocks, names, 'project')
    authority = {
      project: Boolean(projectFlowSnapshot(project, flowName)),
      projectMirror: projectMirror.length > 0,
      projectBindings: projectBindings.length > 0,
    }
    secondary = {
      flowName,
      flowKey,
      remainingProjectMirrorKeys: projectMirror.map((entry) => entry.key),
      remainingBindingPaths: projectBindings.map((reference) => reference.path),
    }
    secondaryVerified =
      expectation === 'present' || (projectMirror.length === 0 && projectBindings.length === 0)
  } else if (surface === 'data') {
    const blockName = typeof payload.blockName === 'string' ? payload.blockName.trim() : ''
    const sourceName = typeof payload.sourceName === 'string' ? payload.sourceName.trim() : ''
    targetId = `${blockName}/${sourceName}`
    authority = {
      project: projectDatasourceSnapshot(project, blockName, sourceName) !== undefined,
    }
  } else if (surface === 'document') {
    targetId = typeof payload.pageId === 'string' ? payload.pageId.trim() : ''
    const workspace = readCogoDocumentWorkspaceFromProject(project)
    authority = {
      projectPageOrder: Boolean(workspace?.graph.pageOrder.includes(targetId)),
      projectNodes: Boolean(
        workspace &&
        Object.entries(workspace.graph.nodes).some(
          ([nodeId, node]) => nodeId === targetId || node.pageId === targetId,
        ),
      ),
    }
  } else {
    throw new Error(`Unsupported Studio core surface: ${String(surface)}`)
  }

  if (!targetId) throw new Error(`No ${surface} server readback target was resolved.`)
  return {
    targetId,
    authority,
    secondary,
    secondaryVerified,
    present: Object.values(authority).some(Boolean),
    verified:
      surface === 'actionflow' && expectation === 'present'
        ? (authority.project || authority.projectMirror) && secondaryVerified
        : verifyCoreSurfaceObservation(authority, expectation, secondaryVerified),
  }
}

interface CoreSurfaceDestructiveImpact {
  expectedChangedPaths: string[]
  exactChanges: StudioCapabilityChangePreview[]
  readbackParams: Record<string, unknown>
}

interface StudioProjectMutationImpact {
  expectedChangedPaths: string[]
  exactChanges: StudioCapabilityChangePreview[]
}

function currentProjectNameForImpact(): string {
  const studio = useStudioStore.getState()
  if (!studio.currentProjectId) {
    throw new Error('Open one exact Studio project before renaming it.')
  }
  const projectMeta = recordOf(studio.projectMeta)
  const projectManifest = recordOf(recordOf(studio.currentProject)?.manifest)
  const candidates = [projectMeta?.name, studio.currentProjectName, projectManifest?.name]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => value.trim())
  if (candidates.length === 0) {
    throw new Error('The current project name is unavailable for an exact rename preview.')
  }
  const [currentName] = candidates
  if (candidates.some((candidate) => candidate !== currentName)) {
    throw new Error('Project name authority is inconsistent. Refresh the project before renaming it.')
  }
  return currentName
}

function buildProjectMutationImpact(
  operationId: string,
  params: Record<string, unknown>,
): StudioProjectMutationImpact | null {
  if (operationId !== 'project.rename') return null
  const proposedName = typeof params.name === 'string' ? params.name.trim() : ''
  if (!proposedName) {
    throw new Error('Provide the exact new project name before requesting approval.')
  }
  return {
    expectedChangedPaths: ['/name'],
    exactChanges: [
      {
        path: '/name',
        label: 'Project name',
        before: currentProjectNameForImpact(),
        proposed: proposedName,
      },
    ],
  }
}

const NULL_PROPOSAL_OPERATION_IDS = new Set([
  'page.details.delete',
  'element.remove',
  'event-binding.remove',
  'behavior.flow.delete',
  'variable.remove',
  'data.source.remove',
  'document.page.delete',
  'project.delete',
])

function declaredImpactProposedValue(
  operationId: string,
  params: Record<string, unknown>,
  path: string,
): unknown {
  if (NULL_PROPOSAL_OPERATION_IDS.has(operationId)) return null
  if (operationId === 'application.patch' && Array.isArray(params.patches)) {
    const patch = params.patches.find(
      (candidate) => recordOf(candidate)?.path === path,
    )
    if (patch) {
      const patchRecord = recordOf(patch)
      return patchRecord?.op === 'remove' ? null : sanitize(patchRecord?.value)
    }
  }
  if (operationId === 'theme.preset.set' || operationId === 'theme.reset') {
    const presetId =
      operationId === 'theme.reset'
        ? 'modern'
        : typeof params.presetId === 'string'
          ? params.presetId
          : ''
    const preset = THEME_PRESETS.find((candidate) => candidate.id === presetId)
    if (path === '/activePresetId') return presetId
    if (path === '/tokens' && preset) {
      return {
        colors: structuredClone(preset.colors),
        fontFamily: preset.fontFamily,
        radius: structuredClone(RADIUS_MAP[preset.radius] ?? RADIUS_MAP.md),
      }
    }
  }
  if (operationId === 'theme.radius.set' && path === '/tokens/radius') {
    const scale = typeof params.radius === 'string' ? params.radius : 'md'
    return structuredClone(resolveRadiusScale(scale))
  }
  if (operationId === 'theme.color.update' && path.startsWith('/tokens/colors/')) {
    return sanitize(params.value)
  }
  if (operationId === 'document.proposal.apply') return 'applied'
  if (operationId === 'document.proposal.reject') return 'rejected'
  if (
    operationId === 'evidence.sufficiency.answer' &&
    path === '/evidence/source-sufficiency/assessment'
  ) {
    const clarificationRequestId =
      typeof params.clarificationRequestId === 'string'
        ? params.clarificationRequestId.trim()
        : ''
    const decision =
      typeof params.decision === 'string' ? params.decision.trim() : ''
    return decision && clarificationRequestId
      ? `Confirm “${decision}” for ${clarificationRequestId}; then regenerate the immutable BFF assessment.`
      : 'Confirm one BFF-provided decision; then regenerate the immutable BFF assessment.'
  }
  if (operationId === 'project.publish.confirm') {
    return {
      action: 'prepare-publication-candidate',
      targetProfileId: 'target-profile.cogo-runtime-web.v1',
    }
  }
  const encodedLeaf = path.split('/').filter(Boolean).at(-1) ?? ''
  const key = decodeURIComponent(encodedLeaf)
  const leafIdentifiesTarget = Object.values(params).some(
    (candidate) =>
      typeof candidate === 'string' && encodeURIComponent(candidate.trim()) === encodedLeaf,
  )
  if (!leafIdentifiesTarget) {
    const exact = findNestedProperty(params, key)
    if (exact !== undefined) return sanitize(exact)
  }
  const contract = getStudioOperationImpactContract(operationId)
  return {
    operationId,
    authority: contract?.authority ?? 'studio-state',
    request: sanitize(params),
  }
}

function buildDeclaredStudioOperationImpact(
  operationId: string,
  params: Record<string, unknown>,
): StudioProjectMutationImpact | null {
  const contract = getStudioOperationImpactContract(operationId)
  if (!contract) return null
  const expectedChangedPaths = buildStudioOperationImpactPaths(operationId, params)
  if (expectedChangedPaths.length === 0) {
    throw new Error(`Studio operation ${operationId} did not resolve an exact changed path.`)
  }
  const exactChanges = expectedChangedPaths.map((path) => {
    const before = authoritativeValueForPath(path)
    const proposed = declaredImpactProposedValue(operationId, params, path)
    return {
      path,
      label: `${contract.surface[0].toUpperCase()}${contract.surface.slice(1)} · ${pathLabel(path)}`,
      ...(before !== undefined ? { before: sanitize(before) } : {}),
      proposed,
    }
  })
  return { expectedChangedPaths, exactChanges }
}

function buildCoreSurfaceDestructiveImpact(
  operationId: string,
  params: Record<string, unknown>,
): CoreSurfaceDestructiveImpact | null {
  const studio = useStudioStore.getState()
  if (operationId === 'page.details.delete') {
    const pageId = typeof params.pageId === 'string' ? params.pageId.trim() : ''
    const editor = useEditorStore.getState()
    const app = editor.app
    if (!pageId || !app) {
      throw new Error('Select an existing application page before deleting it.')
    }
    const editorPageMatches = app.pages.filter((candidate) => candidate.id === pageId)
    if (editorPageMatches.length !== 1) {
      throw new Error(
        `Application page authority conflict: editor expected one ${pageId} page, found ${editorPageMatches.length}.`,
      )
    }
    const page = editorPageMatches[0]
    const path = `/manifest/pages/${encodeURIComponent(pageId)}`
    const remainingPages = app.pages.filter((candidate) => candidate.id !== pageId)
    if (remainingPages.length === 0) {
      throw new Error('Application page deletion must leave at least one editor page.')
    }
    const nextActivePageId =
      editor.activePageId === pageId ? (remainingPages[0]?.id ?? null) : editor.activePageId
    const nextDefaultRoute =
      app.navigation?.defaultRoute === page.route
        ? (remainingPages[0]?.route ?? null)
        : (app.navigation?.defaultRoute ?? null)
    const projectManifest = recordOf(recordOf(studio.currentProject)?.manifest)
    if (!projectManifest || !Array.isArray(projectManifest.pages)) {
      throw new Error(
        'Application page authority conflict: the project manifest page list is unavailable.',
      )
    }
    const projectPages = projectManifest.pages
    const projectPageMatches = projectPages.filter(
      (candidate) => recordOf(candidate)?.id === pageId,
    )
    if (projectPageMatches.length !== 1) {
      throw new Error(
        `Application page authority conflict: project expected one ${pageId} page, found ${projectPageMatches.length}.`,
      )
    }
    const projectPage = projectPageMatches[0]
    const remainingProjectPages = projectPages.filter(
      (candidate) => recordOf(candidate)?.id !== pageId,
    )
    if (remainingProjectPages.length === 0) {
      throw new Error('Application page deletion must leave at least one project-manifest page.')
    }
    const projectNavigation = recordOf(projectManifest.navigation)
    const projectDefaultRoute =
      typeof projectNavigation?.defaultRoute === 'string' ? projectNavigation.defaultRoute : null
    const projectPageRoute =
      typeof recordOf(projectPage)?.route === 'string'
        ? (recordOf(projectPage)?.route as string)
        : null
    const nextProjectDefaultRoute =
      projectPageRoute !== null && projectDefaultRoute === projectPageRoute
        ? (remainingProjectPages
            .map((candidate) => recordOf(candidate)?.route)
            .find((route): route is string => typeof route === 'string') ?? null)
        : projectDefaultRoute
    const exactChanges: StudioCapabilityChangePreview[] = [
      {
        path,
        label: `Application page ${pageId}`,
        before: {
          editor: structuredClone(page),
          project: structuredClone(projectPage),
          editorPageIds: app.pages.map((candidate) => candidate.id),
          projectPageIds: projectPages.map((candidate) => recordOf(candidate)?.id ?? null),
        },
        proposed: null,
      },
    ]
    if (nextActivePageId !== editor.activePageId) {
      exactChanges.push({
        path: '/editor/activePageId',
        label: 'Active application page',
        before: editor.activePageId,
        proposed: nextActivePageId,
      })
    }
    if (
      nextDefaultRoute !== (app.navigation?.defaultRoute ?? null) ||
      nextProjectDefaultRoute !== projectDefaultRoute
    ) {
      exactChanges.push({
        path: '/manifest/navigation/defaultRoute',
        label: 'Default application route',
        before: {
          editor: app.navigation?.defaultRoute ?? null,
          project: projectDefaultRoute,
        },
        proposed: {
          editor: nextDefaultRoute,
          project: nextProjectDefaultRoute,
        },
      })
    }
    return {
      expectedChangedPaths: exactChanges.map((change) => change.path),
      exactChanges,
      readbackParams: {
        surface: 'page',
        pageId,
        expectedActivePageId: nextActivePageId ?? '',
        expectedEditorDefaultRoute: nextDefaultRoute ?? '',
        expectedProjectDefaultRoute: nextProjectDefaultRoute ?? '',
        expectedEditorPageIds: remainingPages.map((candidate) => candidate.id),
        expectedProjectPageIds: remainingProjectPages.map(
          (candidate) => recordOf(candidate)?.id ?? '',
        ),
        expectation: 'absent',
        strict: true,
      },
    }
  }
  if (operationId === 'behavior.flow.delete') {
    const flowId = typeof params.flowId === 'string' ? params.flowId.trim() : ''
    const flow = useFlowEditorStore.getState().flows.find((candidate) => candidate.id === flowId)
    if (!flowId || !flow) {
      throw new Error('Select an existing ActionFlow before deleting it.')
    }
    const requestedBlockName = typeof params.blockName === 'string' ? params.blockName.trim() : ''
    const persisted = projectFlowSnapshot(studio.currentProject, flow.name)
    if (requestedBlockName && persisted?.blockName && requestedBlockName !== persisted.blockName) {
      throw new Error(
        `ActionFlow owner conflict: ${flow.name} belongs to ${persisted.blockName}, not ${requestedBlockName}.`,
      )
    }
    const blockName = persisted?.blockName || requestedBlockName || undefined
    const flowKey = persisted?.flowKey ?? flow.name
    const names = new Set([flow.name, flowKey])
    const bindingReferences = collectFlowReferences(studio.currentProject, names)
    const flowStore = useFlowEditorStore.getState()
    const projectMirror = projectFlowMirrorSnapshots(studio.currentProject, names)
    const editorMirror = editorFlowMirrorSnapshots(names)
    const path = `/flows/${encodeURIComponent(flowId)}`
    const exactChanges: StudioCapabilityChangePreview[] = [
      {
        path,
        label: `ActionFlow ${flow.name} declaration, editor, and canvas`,
        before: {
          editor: structuredClone(flow),
          persisted: persisted?.definition,
          blockName: blockName ?? null,
          flowKey,
          projectMirror,
          projectMirrorFingerprint: stableHash(JSON.stringify(projectMirror)),
          editorMirror,
          canvas: structuredClone(flowStore.flowDataMap[flowId]),
          selected: flowStore.selectedFlowId === flowId,
        },
        proposed: null,
      },
      ...bindingReferences.map((reference) => ({
        path: reference.path,
        label: `ActionFlow binding to ${flow.name}`,
        before: reference.value,
        proposed: null,
      })),
    ]
    return {
      expectedChangedPaths: exactChanges.map((change) => change.path),
      exactChanges,
      readbackParams: {
        surface: 'actionflow',
        flowId,
        flowName: flow.name,
        flowKey,
        ...(blockName ? { blockName } : {}),
        bindingPaths: bindingReferences.map((reference) => reference.path),
        projectMirrorKeys: projectMirror.map((entry) => entry.key),
        expectation: 'absent',
        strict: true,
      },
    }
  }
  if (operationId === 'data.source.remove') {
    const blockName = typeof params.blockName === 'string' ? params.blockName.trim() : ''
    const sourceName = typeof params.name === 'string' ? params.name.trim() : ''
    const datasource = projectDatasourceSnapshot(studio.currentProject, blockName, sourceName)
    if (!blockName || !sourceName || datasource === undefined) {
      throw new Error('Select an existing block datasource before removing it.')
    }
    const path = `/blocks/${encodeURIComponent(blockName)}/data/${encodeURIComponent(sourceName)}`
    return {
      expectedChangedPaths: [path],
      exactChanges: [
        {
          path,
          label: `Datasource ${blockName}/${sourceName}`,
          before: structuredClone(datasource),
          proposed: null,
        },
      ],
      readbackParams: {
        surface: 'data',
        blockName,
        sourceName,
        expectation: 'absent',
        strict: true,
      },
    }
  }
  if (operationId === 'document.page.delete') {
    const pageId = typeof params.pageId === 'string' ? params.pageId.trim() : ''
    const workspace = readCogoDocumentWorkspaceFromProject(studio.currentProject)
    if (!pageId || !workspace?.graph.pageOrder.includes(pageId)) {
      throw new Error('Select an existing document page before deleting it.')
    }
    const nodes = Object.fromEntries(
      Object.entries(workspace.graph.nodes)
        .filter(([nodeId, node]) => nodeId === pageId || node.pageId === pageId)
        .map(([nodeId, node]) => [nodeId, structuredClone(node)]),
    )
    const path = `/cogoDocumentWorkspace/pages/${encodeURIComponent(pageId)}`
    return {
      expectedChangedPaths: [path],
      exactChanges: [
        {
          path,
          label: `Document page ${pageId}`,
          before: { pageId, nodes },
          proposed: null,
        },
      ],
      readbackParams: {
        surface: 'document',
        pageId,
        expectation: 'absent',
        strict: true,
      },
    }
  }
  return null
}

async function readCoreSurfaceState(
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const surface = payload.surface as StudioCoreSurface
  const expectation = payload.expectation === 'absent' ? 'absent' : 'present'
  const strict = payload.strict === true
  const studio = useStudioStore.getState()
  let targetId = ''
  let authority: Record<string, boolean>
  let secondary: Record<string, unknown> = {}
  let secondaryVerified = true

  if (surface === 'page') {
    targetId =
      (typeof payload.pageId === 'string' && payload.pageId.trim()) ||
      useEditorStore.getState().activePageId ||
      ''
    const editor = useEditorStore.getState()
    authority = {
      editor: Boolean(editor.app?.pages.some((page) => page.id === targetId)),
      project: projectPageSnapshot(studio.currentProject, targetId) !== undefined,
    }
    const expectedActivePageId =
      typeof payload.expectedActivePageId === 'string' && payload.expectedActivePageId.length > 0
        ? payload.expectedActivePageId
        : null
    const expectedEditorDefaultRoute =
      typeof payload.expectedEditorDefaultRoute === 'string' &&
      payload.expectedEditorDefaultRoute.length > 0
        ? payload.expectedEditorDefaultRoute
        : null
    const expectedProjectDefaultRoute =
      typeof payload.expectedProjectDefaultRoute === 'string' &&
      payload.expectedProjectDefaultRoute.length > 0
        ? payload.expectedProjectDefaultRoute
        : null
    const expectedEditorPageIds = Array.isArray(payload.expectedEditorPageIds)
      ? payload.expectedEditorPageIds.filter((value): value is string => typeof value === 'string')
      : null
    const expectedProjectPageIds = Array.isArray(payload.expectedProjectPageIds)
      ? payload.expectedProjectPageIds.filter((value): value is string => typeof value === 'string')
      : null
    const actualDefaultRoute = editor.app?.navigation?.defaultRoute ?? null
    const projectManifest = recordOf(studio.currentProject)?.manifest
    const projectDefaultRoute =
      recordOf(recordOf(projectManifest)?.navigation)?.defaultRoute ?? null
    const actualEditorPageIds = editor.app?.pages.map((page) => page.id) ?? []
    const actualProjectPageIds = Array.isArray(recordOf(projectManifest)?.pages)
      ? (recordOf(projectManifest)?.pages as unknown[])
          .map((page) => recordOf(page)?.id)
          .filter((value): value is string => typeof value === 'string')
      : []
    secondary = {
      expectedActivePageId,
      actualActivePageId: editor.activePageId,
      expectedEditorDefaultRoute,
      expectedProjectDefaultRoute,
      actualDefaultRoute,
      projectDefaultRoute,
      expectedEditorPageIds,
      expectedProjectPageIds,
      actualEditorPageIds,
      actualProjectPageIds,
    }
    secondaryVerified =
      editor.activePageId === expectedActivePageId &&
      actualDefaultRoute === expectedEditorDefaultRoute &&
      projectDefaultRoute === expectedProjectDefaultRoute &&
      actualEditorPageIds.length > 0 &&
      actualProjectPageIds.length > 0 &&
      (expectedEditorPageIds === null ||
        JSON.stringify(actualEditorPageIds) === JSON.stringify(expectedEditorPageIds)) &&
      (expectedProjectPageIds === null ||
        JSON.stringify(actualProjectPageIds) === JSON.stringify(expectedProjectPageIds))
  } else if (surface === 'actionflow') {
    targetId =
      (typeof payload.flowId === 'string' && payload.flowId.trim()) ||
      useFlowEditorStore.getState().selectedFlowId ||
      ''
    const flowName =
      (typeof payload.flowName === 'string' && payload.flowName.trim()) || flowNameFromId(targetId)
    const flowKey = (typeof payload.flowKey === 'string' && payload.flowKey.trim()) || flowName
    const blockName = typeof payload.blockName === 'string' ? payload.blockName.trim() : ''
    const flowStore = useFlowEditorStore.getState()
    const bindingReferences = collectFlowReferences(
      studio.currentProject,
      new Set([flowName, flowKey]),
    )
    const projectMirror = projectFlowMirrorSnapshots(
      studio.currentProject,
      new Set([flowName, flowKey]),
    )
    const editorMirror = editorFlowMirrorSnapshots(new Set([flowName, flowKey]))
    authority = {
      editor: flowStore.flows.some((flow) => flow.id === targetId),
      project: Boolean(projectFlowSnapshot(studio.currentProject, flowName)),
      projectMirror: projectMirror.length > 0,
      editorMirror: editorMirror.length > 0,
      canvas: Boolean(flowStore.flowDataMap[targetId]),
      selection: flowStore.selectedFlowId === targetId,
      projectBindings: bindingReferences.some((reference) => reference.authority === 'project'),
      editorBindings: bindingReferences.some((reference) => reference.authority === 'editor'),
    }
    secondary = {
      flowName,
      flowKey,
      blockName: blockName || null,
      expectedBindingPaths: Array.isArray(payload.bindingPaths) ? payload.bindingPaths : [],
      expectedProjectMirrorKeys: Array.isArray(payload.projectMirrorKeys)
        ? payload.projectMirrorKeys
        : [],
      remainingProjectMirrorKeys: projectMirror.map((entry) => entry.key),
      remainingEditorMirrorKeys: editorMirror.map((entry) => entry.key),
      remainingBindingPaths: bindingReferences.map((reference) => reference.path),
    }
  } else if (surface === 'data') {
    const blockName = typeof payload.blockName === 'string' ? payload.blockName.trim() : ''
    const sourceName = typeof payload.sourceName === 'string' ? payload.sourceName.trim() : ''
    targetId = `${blockName}/${sourceName}`
    authority = {
      project:
        projectDatasourceSnapshot(studio.currentProject, blockName, sourceName) !== undefined,
    }
  } else if (surface === 'document') {
    targetId = typeof payload.pageId === 'string' ? payload.pageId.trim() : ''
    const workspace = readCogoDocumentWorkspaceFromProject(studio.currentProject)
    authority = {
      projectPageOrder: Boolean(workspace?.graph.pageOrder.includes(targetId)),
      projectNodes: Boolean(
        workspace &&
        Object.entries(workspace.graph.nodes).some(
          ([nodeId, node]) => nodeId === targetId || node.pageId === targetId,
        ),
      ),
    }
  } else {
    throw new Error(`Unsupported Studio core surface: ${String(payload.surface)}`)
  }

  if (!targetId) throw new Error(`No ${surface} readback target was resolved.`)
  const present = Object.values(authority).some(Boolean)
  const workingCopyVerified = verifyCoreSurfaceObservation(
    authority,
    expectation,
    secondaryVerified,
  )
  if (strict && !workingCopyVerified) {
    throw new Error(
      `${surface} local working-copy readback failed for ${targetId}: expected ${expectation}, authority=${JSON.stringify(authority)}`,
    )
  }
  const capturedAt = new Date().toISOString()
  const localFingerprint = stableHash(
    JSON.stringify({ surface, targetId, expectation, authority, secondary }),
  )
  const localResult = {
    schemaVersion: 'cogo.studio.core-surface-readback.v2',
    surface,
    targetId,
    expectation,
    present,
    readbackScope: 'local-working-copy',
    workingCopyVerified,
    persistenceVerified: false,
    serverPersistence: 'not-verified',
    authority,
    secondary,
    projectId: studio.currentProjectId,
    projectSource: studio.projectSource,
    serverAuthority: null,
    serverSecondary: null,
    serverFingerprint: null,
    persistence: {
      flushAttempted: false,
      flushCompleted: false,
      authoritativeReadback: false,
      authoritativeProjectId: null,
      localDraftReplaced: false,
    },
    capturedAt,
    fingerprint: localFingerprint,
  }

  if (studio.projectSource !== 'server' || !studio.currentProjectId) {
    return localResult
  }

  const projectId = studio.currentProjectId
  if (studio.saveConflict) {
    throw new Error(
      'Server persistence readback is blocked by an unresolved save conflict. Reload or resolve the conflict first.',
    )
  }
  await flushEditorFullPersist()
  const stateAfterEditorFlush = useStudioStore.getState()
  if (stateAfterEditorFlush.saveError || stateAfterEditorFlush.saveConflict) {
    throw new Error(
      stateAfterEditorFlush.saveConflict
        ? 'Server persistence readback is blocked by a save conflict from the full editor persist.'
        : `Server persistence readback could not flush the full editor persist: ${stateAfterEditorFlush.saveError}`,
    )
  }
  await flushSaves()

  const stateAfterFlush = useStudioStore.getState()
  if (
    stateAfterFlush.projectSource !== 'server' ||
    stateAfterFlush.currentProjectId !== projectId
  ) {
    throw new Error(
      'Studio project changed while flushing the server persistence readback. Retry on the intended project.',
    )
  }
  if (stateAfterFlush.saveError || stateAfterFlush.saveConflict || stateAfterFlush.isSaving) {
    throw new Error(
      stateAfterFlush.saveConflict
        ? 'Server persistence readback is blocked by a save conflict from the queued save lane.'
        : stateAfterFlush.saveError
          ? `Server persistence readback could not flush the queued save lane: ${stateAfterFlush.saveError}`
          : 'Server persistence readback reached the authoritative GET while a save was still in flight.',
    )
  }

  // loadProject performs an authoritative Worker GET and returns a detached
  // LoadedApp. Deliberately do not call openServerProject/loadApp here: the
  // local draft remains the user's working copy while this detached snapshot
  // is compared with the requested target expectation.
  const localDraft = stateAfterFlush.currentProject
  const authoritative = await loadProject(projectId)
  const stateAfterReadback = useStudioStore.getState()
  if (
    stateAfterReadback.projectSource !== 'server' ||
    stateAfterReadback.currentProjectId !== projectId ||
    stateAfterReadback.currentProject !== localDraft
  ) {
    throw new Error(
      'Studio project changed during the authoritative server readback. Retry on the intended project.',
    )
  }

  const server = observeAuthoritativeCoreSurface(authoritative.app, surface, expectation, payload)
  if (strict && !server.verified) {
    throw new Error(
      `${surface} server persistence readback failed for ${server.targetId}: expected ${expectation}, authority=${JSON.stringify(server.authority)}`,
    )
  }
  const serverCapturedAt = new Date().toISOString()
  const serverFingerprint = stableHash(
    JSON.stringify({
      surface,
      targetId: server.targetId,
      expectation,
      authority: server.authority,
      secondary: server.secondary,
    }),
  )
  return {
    ...localResult,
    targetId: server.targetId,
    present: server.present,
    readbackScope: 'server-authoritative',
    persistenceVerified: server.verified,
    serverPersistence: server.verified ? 'verified' : 'verification-failed',
    serverAuthority: server.authority,
    serverSecondary: server.secondary,
    serverFingerprint,
    persistence: {
      flushAttempted: true,
      flushCompleted: true,
      authoritativeReadback: true,
      authoritativeProjectId: projectId,
      localDraftReplaced: false,
      serverCapturedAt,
    },
    capturedAt: serverCapturedAt,
    fingerprint: stableHash(`${localFingerprint}:${serverFingerprint}`),
  }
}

function proposedValueForPath(plan: StudioCapabilityPlan, path: string): unknown {
  const key = decodeURIComponent(path.split('/').filter(Boolean).at(-1) ?? '')
  for (const command of [...plan.commands].reverse()) {
    const found = findNestedProperty(command.params, key)
    if (found !== undefined) return sanitize(found)
    if (key === 'pageOrder') {
      const toIndex = findNestedProperty(command.params, 'toIndex')
      if (toIndex !== undefined) return { toIndex }
    }
  }
  return undefined
}

function readRecordPath(value: unknown, segments: string[]): unknown {
  let current = value
  for (const segment of segments) {
    if (Array.isArray(current)) {
      const index = Number(segment)
      if (!Number.isInteger(index) || index < 0 || index >= current.length) return undefined
      current = current[index]
      continue
    }
    const record = recordOf(current)
    if (!record || !Object.prototype.hasOwnProperty.call(record, segment)) return undefined
    current = record[segment]
  }
  return current
}

function authoritativeValueForPath(path: string): unknown {
  const studio = useStudioStore.getState()
  const editor = useEditorStore.getState()
  const pageMatch = /^\/manifest\/pages\/([^/]+)\/([^/]+)$/.exec(path)
  if (pageMatch) {
    const [, encodedPageId, encodedKey] = pageMatch
    const pageId = decodeURIComponent(encodedPageId)
    const key = decodeURIComponent(encodedKey)
    const page = useEditorStore.getState().app?.pages.find((candidate) => candidate.id === pageId)
    return page ? sanitize((page as unknown as Record<string, unknown>)[key]) : undefined
  }
  const pageRootMatch = /^\/manifest\/pages\/([^/]+)$/.exec(path)
  if (pageRootMatch) {
    const pageId = decodeURIComponent(pageRootMatch[1])
    return (
      editor.app?.pages.find((candidate) => candidate.id === pageId) ??
      projectPageSnapshot(studio.currentProject, pageId)
    )
  }
  const blockMatch = /^\/blocks\/([^/]+)\/(ui|data|variables)(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?$/.exec(
    path,
  )
  if (blockMatch) {
    const [, encodedBlockName, domain, encodedFirst, encodedSecond, encodedThird] = blockMatch
    const blockName = decodeURIComponent(encodedBlockName)
    if (domain === 'ui') {
      const block = editor.blocks[blockName]
      if (!block) return undefined
      if (!encodedFirst) return block.ui.root
      const element = findElementInTree(block.ui.root, decodeURIComponent(encodedFirst))
      if (!element) return undefined
      const nested = [encodedSecond, encodedThird]
        .filter((entry): entry is string => Boolean(entry))
        .map(decodeURIComponent)
      return nested.length > 0 ? readRecordPath(element, nested) : element
    }
    if (domain === 'data' && encodedFirst) {
      return projectDatasourceSnapshot(
        studio.currentProject,
        blockName,
        decodeURIComponent(encodedFirst),
      )
    }
    const projectBlock = recordOf(recordOf(studio.currentProject)?.blocks)?.[blockName]
    const nested = [domain, encodedFirst, encodedSecond, encodedThird]
      .filter((entry): entry is string => Boolean(entry))
      .map(decodeURIComponent)
    return readRecordPath(projectBlock, nested)
  }
  const flowMatch = /^\/flows(?:\/([^/]+))?(?:\/(.+))?$/.exec(path)
  if (flowMatch) {
    if (!flowMatch[1]) return useFlowEditorStore.getState().flows
    const flowId = decodeURIComponent(flowMatch[1])
    const flow = useFlowEditorStore
      .getState()
      .flows.find((candidate) => candidate.id === flowId || candidate.name === flowId)
    return flowMatch[2]
      ? readRecordPath(flow, flowMatch[2].split('/').map(decodeURIComponent))
      : flow
  }
  if (path === '/name') {
    try {
      return currentProjectNameForImpact()
    } catch {
      return undefined
    }
  }
  if (path === '/description' || path === '/icon') {
    const key = path.slice(1)
    return recordOf(studio.projectMeta)?.[key] ?? recordOf(studio.currentProject)?.[key]
  }
  if (path === '/activePresetId') return studio.activePresetId
  if (path === '/tokens') return recordOf(studio.currentProject)?.tokens
  if (path.startsWith('/tokens/')) {
    return readRecordPath(
      recordOf(studio.currentProject)?.tokens,
      path.slice('/tokens/'.length).split('/').map(decodeURIComponent),
    )
  }
  if (path === '/sourceWorkspace' || path.startsWith('/sourceWorkspace/')) {
    const nested = path
      .slice('/sourceWorkspace'.length)
      .split('/')
      .filter(Boolean)
      .map(decodeURIComponent)
    return readRecordPath(studio.openPencilSourceWorkspace, nested)
  }
  if (path === '/cogoDocumentWorkspace' || path.startsWith('/cogoDocumentWorkspace/')) {
    const workspace = readCogoDocumentWorkspaceFromProject(studio.currentProject)
    const nested = path
      .slice('/cogoDocumentWorkspace'.length)
      .split('/')
      .filter(Boolean)
      .map(decodeURIComponent)
    if (nested[0] === 'pages' && nested[1]) {
      const pageId = nested[1]
      return {
        pageId,
        nodes: workspace
          ? Object.fromEntries(
              Object.entries(workspace.graph.nodes).filter(
                ([nodeId, node]) => nodeId === pageId || node.pageId === pageId,
              ),
            )
          : undefined,
      }
    }
    if (nested[0] === 'nodes') nested.splice(0, 1, 'graph', 'nodes')
    if (nested[0] === 'pageOrder') nested.splice(0, 1, 'graph', 'pageOrder')
    return readRecordPath(workspace, nested)
  }
  return undefined
}

function beforeValueForPath(_plan: StudioCapabilityPlan, path: string): unknown {
  return authoritativeValueForPath(path)
}

function exactChangeForPath(
  plan: StudioCapabilityPlan,
  path: string,
): StudioCapabilityChangePreview | undefined {
  for (const command of [...plan.commands].reverse()) {
    const change = command.exactChanges?.find((entry) => entry.path === path)
    if (change) return change
  }
  return undefined
}

function capabilityTargetLabel(plan: StudioCapabilityPlan): string | null {
  const keys = [
    'pageId',
    'documentNodeId',
    'nodeId',
    'flowId',
    'elementId',
    'sourceName',
    'workspaceId',
    'proposalId',
    'target',
  ]
  for (const key of keys) {
    for (const command of plan.commands) {
      const value = findNestedProperty(command.params, key)
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
  }
  return null
}

function fallbackRisk(plan: StudioCapabilityPlan): StudioCapabilityImpactPreview['risk'] {
  const tools = plan.commands.map((command) => command.tool)
  if (tools.some((tool) => /delete_page|deletePage/i.test(tool))) {
    return 'destructive'
  }
  if (
    tools.some((tool) =>
      /publishProject|\.export(?:_|$)|runtime_proof|visual_judgement/i.test(tool),
    )
  ) {
    return 'external'
  }
  return 'reversible'
}

function buildImpactPreview(plan: StudioCapabilityPlan): StudioCapabilityImpactPreview {
  const operationId = operationIdFromPlan(plan)
  const operation = operationId ? getStudioChatOperationContract(operationId) : undefined
  const impactContract = operationId ? getStudioOperationImpactContract(operationId) : undefined
  const targetLabel = capabilityTargetLabel(plan)
  const risk = operation?.risk ?? fallbackRisk(plan)
  const confirmation =
    operation?.confirmation ??
    (plan.commands.some((command) => /publishProject|delete_page|deletePage/i.test(command.tool))
      ? 'typed'
      : 'user')
  const paths = changedPaths(plan)
  const changes = paths.map((path) => {
    const exact = exactChangeForPath(plan, path)
    if (exact) {
      return {
        path,
        label: exact.label || pathLabel(path),
        ...(Object.prototype.hasOwnProperty.call(exact, 'before')
          ? { before: sanitize(exact.before) }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(exact, 'proposed')
          ? { proposed: sanitize(exact.proposed) }
          : {}),
      }
    }
    const before = beforeValueForPath(plan, path)
    const proposed = proposedValueForPath(plan, path)
    return {
      path,
      label: pathLabel(path),
      ...(before !== undefined ? { before } : {}),
      ...(proposed !== undefined ? { proposed } : {}),
    }
  })
  return {
    operationId,
    ...(impactContract
      ? {
          affectedSurfaces: [
            ...(impactContract.affectedSurfaces ?? [impactContract.surface]),
          ],
        }
      : {}),
    summary: targetLabel ? `${plan.label} on ${targetLabel}` : plan.label,
    targetLabel,
    risk,
    confirmation,
    ...(confirmation === 'typed'
      ? { confirmationPhrase: risk === 'destructive' ? 'DELETE' : 'PUBLISH' }
      : {}),
    changes,
    readback:
      operation?.readback.description ??
      'Refresh the owning Studio surface and retain the resulting readback.',
    undo:
      operation?.undo.description ??
      (risk === 'external'
        ? 'External effects require a separate compensating operation.'
        : 'Undo the latest confirmed Studio mutation.'),
    persistence: canPersistPlan(plan) ? 'session' : 'memory-only',
    currentState: captureStudioCapabilityState(),
  }
}

function pathLabel(path: string): string {
  return path
    .split('/')
    .filter(Boolean)
    .map((part) => decodeURIComponent(part).replaceAll(/[-_]/g, ' '))
    .join(' / ')
}

function cancelEvictedDurablePending(item: PendingCapabilityPlan): Promise<void> {
  if (item.journalMode !== 'durable') return Promise.resolve()
  return cancelStudioOrchestrationJournal({
    projectId: item.orchestrationPlan.projectId,
    runId: item.orchestrationPlan.runId,
    reason: 'studio-user-cancelled-confirmation',
  })
    .then(() => undefined)
    .catch(() => undefined)
}

async function cancelExecutorOwnedPending(
  item: PendingCapabilityPlan,
  options: { cancelActive: boolean },
): Promise<boolean> {
  const runId = item.orchestrationPlan.runId
  const active = activeStep5Executors.get(runId)
  if (active) {
    if (options.cancelActive) {
      active.cancel(runId, 'studio-user-cancelled-confirmation')
    }
    return false
  }
  try {
    const { executor } = createStep5Executor({
      item,
      bus: mcpBus,
      approvalSource: 'studio-user-action',
      approvedAt: new Date().toISOString(),
    })
    const result = await executor.cancelPersisted(
      item.orchestrationPlan,
      'studio-user-cancelled-confirmation',
    )
    if (result.status !== 'cancelled') return false
    const execution = projectStep5Execution({
      item,
      result,
      approvalSource: 'not-approved',
    })
    upsertStep5Execution(item.scope, execution)
    pending.delete(item.confirmationId)
    step5DurableReferences.delete(item.confirmationId)
    persistStep5DurableReferences(item.scope)
    recordConfirmationLifecycle(item.scope, {
      confirmationId: item.confirmationId,
      status: 'cancelled',
      executionId: execution.executionId,
    })
    return true
  } catch {
    return false
  }
}

async function cleanExpiredPending(options: { reserveSlot?: boolean } = {}): Promise<void> {
  const now = Date.now()
  const touchedScopes = new Map<string, StudioCapabilityScope>()
  const evicted: PendingCapabilityPlan[] = []
  for (const [id, item] of pending) {
    if (item.expiresAt <= now && !confirmationsInFlight.has(id)) {
      if (item.lifecycleOwner === 'executor-v2') {
        await cancelExecutorOwnedPending(item, { cancelActive: true })
        continue
      }
      pending.delete(id)
      recordConfirmationLifecycle(item.scope, {
        confirmationId: id,
        status: 'failed',
        failureCode: 'CONFIRMATION_MISSING_OR_EXPIRED',
        error: 'Confirmation is missing or expired.',
      })
      touchedScopes.set(item.scope.key, item.scope)
      evicted.push(item)
    }
  }
  const attemptedExecutorEvictions = new Set<string>()
  while (
    pending.size > MAX_PENDING ||
    (options.reserveSlot === true && pending.size >= MAX_PENDING)
  ) {
    const oldest = [...pending.values()]
      .filter(
        (item) =>
          !confirmationsInFlight.has(item.confirmationId) &&
          !attemptedExecutorEvictions.has(item.confirmationId),
      )
      .sort((left, right) => left.createdAt - right.createdAt)[0]
    if (!oldest) break
    if (oldest.lifecycleOwner === 'executor-v2') {
      attemptedExecutorEvictions.add(oldest.confirmationId)
      await cancelExecutorOwnedPending(oldest, { cancelActive: false })
      continue
    }
    pending.delete(oldest.confirmationId)
    recordConfirmationLifecycle(oldest.scope, {
      confirmationId: oldest.confirmationId,
      status: 'failed',
      failureCode: 'CONFIRMATION_MISSING_OR_EXPIRED',
      error: 'Confirmation is missing or expired.',
    })
    touchedScopes.set(oldest.scope.key, oldest.scope)
    evicted.push(oldest)
  }
  for (const scope of touchedScopes.values()) persistPendingCapabilities(scope)
  if (evicted.length > 0) notifyGatewayListeners()
  await Promise.all(evicted.map(cancelEvictedDurablePending))
}

function hasAuthenticatedDurableJournalScope(scope: StudioCapabilityScope): boolean {
  const studio = useStudioStore.getState()
  return Boolean(
    scope.projectId &&
    scope.principalId !== 'anonymous' &&
    studio.user?.id === scope.principalId &&
    studio.session?.access_token?.trim(),
  )
}

function orchestrationProjectId(scope: StudioCapabilityScope): string {
  return scope.projectId ?? `studio-session-${stableHash(scope.sessionId)}`
}

function isMutatingCommand(bus: StudioMcpBus, command: StudioCapabilityCommand): boolean {
  if (command.requiresConfirmation !== undefined) {
    return command.requiresConfirmation
  }
  const manifest = bus.getManifest().find((item) => item.name === command.tool)
  return manifest?.metadata?.readOnly !== true
}

function operationIdForCommand(
  plan: StudioCapabilityPlan,
  command: StudioCapabilityCommand,
): string | null {
  if (command.operationId && getStudioOperationImpactContract(command.operationId)) {
    return command.operationId
  }
  const planOperationId = operationIdFromPlan(plan)
  if (planOperationId && getStudioOperationImpactContract(planOperationId)) {
    return planOperationId
  }
  const candidates = listStudioChatOperationContracts().filter(
    (operation) =>
      operation.status === 'ready' &&
      operation.readOnly !== true &&
      operation.webMcpToolName === command.tool &&
      Boolean(getStudioOperationImpactContract(operation.id)),
  )
  if (candidates.length === 0) return null
  const expected = [...new Set(command.expectedChangedPaths ?? [])].sort()
  if (expected.length > 0) {
    const exact = candidates.find((candidate) => {
      const paths = buildStudioOperationImpactPaths(candidate.id, command.params).sort()
      return JSON.stringify(paths) === JSON.stringify(expected)
    })
    if (exact) return exact.id
  }
  return candidates[0]?.id ?? null
}

function exactImpactIsComplete(command: StudioCapabilityCommand): boolean {
  if (!command.exactChanges?.length) return false
  const paths = [...new Set(command.exactChanges.map((change) => change.path))]
  if (paths.some((path) => !path.startsWith('/'))) return false
  if (
    command.exactChanges.some(
      (change) =>
        !Object.prototype.hasOwnProperty.call(change, 'before') &&
        !Object.prototype.hasOwnProperty.call(change, 'proposed'),
    )
  ) {
    return false
  }
  const expected = [...new Set(command.expectedChangedPaths ?? paths)].sort()
  return JSON.stringify(paths.sort()) === JSON.stringify(expected)
}

function fallbackExactImpact(command: StudioCapabilityCommand): StudioProjectMutationImpact | null {
  const paths = [...new Set(command.expectedChangedPaths ?? [])]
  if (paths.length === 0) return null
  return {
    expectedChangedPaths: paths,
    exactChanges: paths.map((path) => {
      const before = authoritativeValueForPath(path)
      const proposed = proposedValueForPath(
        {
          planId: 'impact-preview',
          label: command.tool,
          source: 'studio-ui',
          commands: [command],
        },
        path,
      )
      return {
        path,
        label: pathLabel(path),
        ...(before !== undefined ? { before: sanitize(before) } : {}),
        proposed: proposed !== undefined ? proposed : sanitize(command.params),
      }
    }),
  }
}

function prepareExactImpactPlan(
  inputPlan: StudioCapabilityPlan,
  bus: StudioMcpBus,
): StudioCapabilityPlan {
  const commands = inputPlan.commands.map((command) => {
    if (!isMutatingCommand(bus, command)) return command
    if (exactImpactIsComplete(command)) return command
    const operationId = operationIdForCommand(inputPlan, command)
    const impact = operationId
      ? buildDeclaredStudioOperationImpact(operationId, command.params)
      : fallbackExactImpact(command)
    if (!impact?.exactChanges.length) {
      throw new Error(
        `Exact impact is required before ${command.tool} can request approval. Add a registered Studio operation impact contract.`,
      )
    }
    return {
      ...command,
      ...(operationId ? { operationId } : {}),
      expectedChangedPaths: impact.expectedChangedPaths,
      exactChanges: impact.exactChanges,
    }
  })
  return { ...inputPlan, commands }
}

function requiresCapabilityApprovalGrant(
  bus: StudioMcpBus,
  command: StudioCapabilityCommand,
): boolean {
  return (
    bus.requiresCapabilityApproval(command.tool) ||
    HANDLER_ENFORCED_APPROVAL_TOOLS.has(command.tool)
  )
}

function changedPaths(plan: StudioCapabilityPlan): string[] {
  return Array.from(
    new Set(
      plan.commands.flatMap((command) => [
        ...(command.expectedChangedPaths ?? []),
        ...(command.exactChanges?.map((change) => change.path) ?? []),
      ]),
    ),
  )
}

function validateDesignMutationConfirmationPreview(plan: StudioCapabilityPlan): string | null {
  const command = plan.commands.find(
    (entry) => entry.tool === 'cogo.studio.apply_openpencil_cogo_design_mutation',
  )
  if (!command || !Object.prototype.hasOwnProperty.call(command.params, 'proposal')) {
    return null
  }
  const state = useStudioStore.getState()
  if (!state.currentProjectId || !state.currentProject || !state.openPencilSourceWorkspace) {
    return 'The active Coogo project or editable OpenPencil workspace is no longer available.'
  }
  try {
    const current = buildOpenPencilCogoDesignMutationImpactPreview({
      proposal: command.params.proposal,
      workspace: state.openPencilSourceWorkspace,
      currentProjectId: state.currentProjectId,
      currentProject: state.currentProject,
    })
    const currentExactChanges = current.changes.map((change) => ({
      ...change,
      label: pathLabel(change.path),
    }))
    if (
      JSON.stringify(current.changedPaths) !== JSON.stringify(command.expectedChangedPaths) ||
      JSON.stringify(currentExactChanges) !== JSON.stringify(command.exactChanges)
    ) {
      return 'The Coogo target or source evidence changed after the confirmation preview.'
    }
  } catch (error) {
    return `The design mutation preconditions changed: ${error instanceof Error ? error.message : String(error)}`
  }
  return null
}

function validateCoreSurfaceConfirmationPreview(plan: StudioCapabilityPlan): string | null {
  const operationId = operationIdFromPlan(plan)
  if (!operationId) return null
  const mutationCommand = plan.commands.find(
    (command) => command.tool !== CORE_SURFACE_READBACK_TOOL,
  )
  if (!mutationCommand?.exactChanges?.length) return null
  try {
    const current = buildCoreSurfaceDestructiveImpact(operationId, mutationCommand.params)
    if (!current) return null
    const readbackCommand = plan.commands.find(
      (command) => command.tool === CORE_SURFACE_READBACK_TOOL,
    )
    if (
      JSON.stringify(current.expectedChangedPaths) !==
        JSON.stringify(mutationCommand.expectedChangedPaths) ||
      JSON.stringify(current.exactChanges) !== JSON.stringify(mutationCommand.exactChanges) ||
      JSON.stringify(current.readbackParams) !== JSON.stringify(readbackCommand?.params)
    ) {
      return 'The Page, ActionFlow, Data, or Document target changed after the confirmation preview.'
    }
  } catch (error) {
    return `The core surface mutation preconditions changed: ${error instanceof Error ? error.message : String(error)}`
  }
  return null
}

function validateProjectMutationConfirmationPreview(plan: StudioCapabilityPlan): string | null {
  const operationId = operationIdFromPlan(plan)
  if (operationId !== 'project.rename') return null
  const mutationCommand = plan.commands[0]
  if (!mutationCommand?.exactChanges?.length) {
    return 'The project rename proposal no longer has an exact change preview.'
  }
  try {
    const current = buildProjectMutationImpact(operationId, mutationCommand.params)
    if (
      !current ||
      JSON.stringify(current.expectedChangedPaths) !==
        JSON.stringify(mutationCommand.expectedChangedPaths) ||
      JSON.stringify(current.exactChanges) !== JSON.stringify(mutationCommand.exactChanges)
    ) {
      return 'The project name changed after the confirmation preview.'
    }
  } catch (error) {
    return `The project rename preconditions changed: ${error instanceof Error ? error.message : String(error)}`
  }
  return null
}

function validateDeclaredOperationConfirmationPreview(
  plan: StudioCapabilityPlan,
  bus: StudioMcpBus,
): string | null {
  const specialized = new Set([
    'source.figma.apply_design_mutation',
    'page.details.delete',
    'behavior.flow.delete',
    'data.source.remove',
    'document.page.delete',
    'project.rename',
  ])
  try {
    for (const command of plan.commands) {
      if (!isMutatingCommand(bus, command)) continue
      const operationId = operationIdForCommand(plan, command)
      if (!operationId) {
        for (const change of command.exactChanges ?? []) {
          if (!Object.prototype.hasOwnProperty.call(change, 'before')) continue
          const currentBefore = authoritativeValueForPath(change.path)
          if (JSON.stringify(sanitize(currentBefore)) !== JSON.stringify(change.before)) {
            return 'The exact Studio impact changed after the confirmation preview.'
          }
        }
        continue
      }
      if (specialized.has(operationId)) continue
      const current = buildDeclaredStudioOperationImpact(operationId, command.params)
      if (
        !current ||
        JSON.stringify(current.expectedChangedPaths) !==
          JSON.stringify(command.expectedChangedPaths) ||
        JSON.stringify(current.exactChanges) !== JSON.stringify(command.exactChanges)
      ) {
        return 'The exact Studio impact changed after the confirmation preview.'
      }
    }
  } catch (error) {
    return `The Studio operation impact preconditions changed: ${
      error instanceof Error ? error.message : String(error)
    }`
  }
  return null
}

/**
 * wave-3 (Lane A) — commit-time re-verification, closing the window measured
 * at docs/superpowers/specs/2026-08-17-studio-slice4-concurrent-write.md
 * cell (b). validateDeclaredOperationConfirmationPreview (above) runs this
 * SAME before-vs-live comparison (its no-operationId branch) exactly once,
 * synchronously, before the apply loop starts — but there is a real await
 * gap between that check and the writer's actual mutation, because the
 * writer lives inside a WebMCP tool handler dispatched through the bus's
 * middleware chain (studio-mcp-bus.ts, out of this module's control). This
 * function is exported so a tool handler can invoke it as the LAST
 * synchronous step before it performs its write — see
 * register-studio-tools.ts's `updatePageMeta` handler, the only handler
 * wired to it so far (scoped to the measured seam, not a general lock/merge
 * mechanism). A mismatch here means the document moved AGAIN after the
 * pre-apply check already confirmed it matched.
 */
export function exactChangesDriftFromLiveState(
  exactChanges: StudioCapabilityChangePreview[] | undefined,
): string | null {
  for (const change of exactChanges ?? []) {
    if (!Object.prototype.hasOwnProperty.call(change, 'before')) continue
    const currentBefore = authoritativeValueForPath(change.path)
    if (JSON.stringify(sanitize(currentBefore)) !== JSON.stringify(change.before)) {
      return 'The exact Studio impact changed after the confirmation preview, immediately before the write committed.'
    }
  }
  return null
}

/**
 * wave-3 (Lane A) — marker prefix so a tool handler can signal a
 * commit-time staleness failure through the existing McpResult.error string
 * channel. This module does not control studio-mcp-bus.ts's error handling
 * (it only forwards a thrown Error's `.message`, not custom fields), so the
 * code travels as a recognizable prefix on the message instead. See
 * exactChangesDriftFromLiveState() above and its throw site in
 * register-studio-tools.ts's `updatePageMeta` handler; stripped back off in
 * executeConfirmedPlan's `!result.success` handling below.
 */
export const CONFIRMATION_STALE_AT_COMMIT_MARKER =
  'STUDIO_CONFIRMATION_STALE_AT_COMMIT::'

function preflightPlan(bus: StudioMcpBus, plan: StudioCapabilityPlan): string[] {
  const errors: string[] = []
  if (!plan.commands.length) errors.push('Capability plan has no commands.')
  for (const command of plan.commands) {
    if (!bus.hasTool(command.tool)) {
      errors.push(`WebMCP tool is not registered: ${command.tool}`)
    }
    if (command.readback && !bus.hasTool(command.readback.tool)) {
      errors.push(`Readback tool is not registered: ${command.readback.tool}`)
    }
    if (isMutatingCommand(bus, command) && !exactImpactIsComplete(command)) {
      errors.push(`Exact impact is incomplete for mutating tool: ${command.tool}`)
    }
  }
  return errors
}

function appendExecution(scope: StudioCapabilityScope, execution: StudioCapabilityExecution): void {
  const executions = getScopedExecutions(scope)
  executions.push(execution)
  if (executions.length > MAX_EXECUTIONS) executions.shift()
  persistExecutions(scope, executions)
  notifyGatewayListeners()
}

function isAuthenticatedStep5PageRename(
  plan: StudioCapabilityPlan,
  bus: StudioMcpBus,
  scope: StudioCapabilityScope,
): boolean {
  return (
    hasAuthenticatedDurableJournalScope(scope) &&
    plan.commands.length === 1 &&
    plan.commands[0]?.tool === 'cogo.studio.updatePageMeta' &&
    isMutatingCommand(bus, plan.commands[0])
  )
}

function step5ConfirmationPhrase(plan: StudioCapabilityPlan): string {
  const pageId = plan.commands[0]?.params.pageId
  return `RENAME ${typeof pageId === 'string' ? pageId.trim().toUpperCase() : ''}`.trim()
}

function step5ImpactPreview(plan: StudioCapabilityPlan, confirmationPhrase: string): StudioCapabilityImpactPreview {
  return {
    ...buildImpactPreview(plan),
    confirmation: 'typed',
    confirmationPhrase,
  }
}

function step5OrchestrationReference(plan: CoogoOrchestrationPlanV2): StudioCapabilityOrchestrationReference {
  return {
    planSchemaVersion: plan.schemaVersion,
    eventSchemaVersion: COOGO_EXECUTION_EVENT_V2_VERSION,
    runId: plan.runId,
    ...(plan.parentRunId ? { parentRunId: plan.parentRunId } : {}),
    nodeIds: plan.nodes.map((node) => node.id),
  }
}

function executorError(result: CoogoExecutionResult): {
  code?: StudioCapabilityExecutionFailureCode
  message: string
} {
  const failed = Object.values(result.nodes).find((node) => node.status === 'failed')
  // task-1 — pass through whatever code the failed node's CoogoExecutionError
  // actually carried (previously narrowed to only 2 of the ~26 codes; see the
  // StudioCapabilityExecutionFailureCode comment above). fix-round-1 (I3):
  // validated through narrowExecutionFailureCode(), not cast.
  const code = failed?.error?.code
  return {
    ...(code ? { code: narrowExecutionFailureCode(code) } : {}),
    message:
      failed?.error?.message ??
      (result.status === 'cancelled' ? 'The Studio orchestration was cancelled.' : 'The Studio orchestration failed.'),
  }
}

function projectStep5Rollback(
  result: CoogoExecutionResult,
): StudioCapabilityExecution['rollback'] | undefined {
  if (result.status !== 'failed') return undefined
  for (const failedNode of ['n4', 'n5'] as const) {
    const compensation = result.nodes[`r1-undo-${failedNode}-failure`]
    if (
      !compensation ||
      compensation.status === 'pending' ||
      compensation.status === 'skipped' ||
      compensation.status === 'cancelled'
    ) {
      continue
    }
    const verification = result.nodes[`r2-verify-${failedNode}-restore`]
    const compensationOutput = recordOf(compensation.output)
    const verificationOutput = recordOf(verification?.output)
    const disposition =
      compensation.status === 'completed' &&
      compensationOutput?.kind === 'compensation-result' &&
      compensationOutput.verified === true &&
      (compensationOutput.disposition === 'already-restored' ||
        compensationOutput.disposition === 'owned-undo')
        ? compensationOutput.disposition
        : null
    const restored =
      disposition !== null &&
      verification?.status === 'completed' &&
      verificationOutput?.kind === 'exact-impact' &&
      verificationOutput.mode === 'assert' &&
      verificationOutput.verified === true
    return {
      attempted: disposition === 'already-restored' ? false : true,
      succeeded: restored,
      ...(!restored
        ? {
            error:
              verification?.error?.message ??
              compensation.error?.message ??
              'The durable run did not verify restoration of the pre-run state.',
          }
        : {}),
    }
  }
  return undefined
}

function projectStep5Execution(input: {
  item: PendingCapabilityPlan
  result: CoogoExecutionResult
  approvalSource: StudioCapabilityExecution['approval']['source']
  approvedAt?: string
}): StudioCapabilityExecution {
  const { item, result } = input
  const error = result.status === 'failed' || result.status === 'cancelled' ? executorError(result).message : undefined
  const rollback = projectStep5Rollback(result)
  return {
    executionId: `durable:${result.runId}`,
    planId: item.plan.planId,
    label: item.plan.label,
    source: item.plan.source,
    projectId: item.scope.projectId,
    sessionId: item.scope.sessionId,
    status: result.status === 'completed' ? 'executed' : result.status === 'cancelled' ? 'cancelled' : result.status,
    tools: item.plan.commands.map((command) => command.tool),
    changedPaths: changedPaths(item.plan),
    before: structuredClone(item.impact.currentState),
    after: captureStudioCapabilityState(),
    results: item.plan.commands.map((command) => ({
      tool: command.tool,
      success: result.status === 'completed',
      ...(result.status === 'failed' ? { error } : {}),
    })),
    undoAvailable: false,
    ...(rollback ? { rollback } : {}),
    approval: {
      required: true,
      source: input.approvalSource,
      confirmationId: item.confirmationId,
      ...(input.approvedAt ? { approvedAt: input.approvedAt } : {}),
    },
    createdAt: new Date(item.createdAt).toISOString(),
    ...(error ? { error } : {}),
    ...(result.status === 'failed' && executorError(result).code
      ? { executionFailureCode: executorError(result).code }
      : {}),
    orchestration: step5OrchestrationReference(item.orchestrationPlan as CoogoOrchestrationPlanV2),
    events: structuredClone(result.events),
  }
}

function upsertStep5Execution(scope: StudioCapabilityScope, execution: StudioCapabilityExecution): void {
  const executions = getScopedExecutions(scope)
  const index = executions.findIndex((candidate) => candidate.orchestration.runId === execution.orchestration.runId)
  if (index >= 0) executions[index] = execution
  else executions.push(execution)
  if (executions.length > MAX_EXECUTIONS) executions.shift()
  persistExecutions(scope, executions)
  notifyGatewayListeners()
}

function authenticatedN1Envelope(input: {
  item: PendingCapabilityPlan
  loaded: Awaited<ReturnType<StudioCoogoExecutionJournal['load']>>
}): unknown | null {
  const output = input.loaded?.snapshot.nodes['n1-impact-before']?.output
  const gate = (input.item.orchestrationPlan as CoogoOrchestrationPlanV2).nodes.find(
    (node) => node.id === 'n2-human-gate',
  )?.humanGate
  if (!output || !gate) return null
  return {
    planId: input.item.orchestrationPlan.planId,
    runId: input.item.orchestrationPlan.runId,
    projectId: input.item.orchestrationPlan.projectId,
    nodeId: 'n1-impact-before',
    admittedTarget: structuredClone(gate.target),
    output: structuredClone(output),
  }
}

function createStep5Executor(input: {
  item: PendingCapabilityPlan
  bus: StudioMcpBus
  approvalSource: StudioCapabilityApprovalSource
  approvedAt: string
  recoveredRunningN4?: boolean
}): {
  executor: CoogoOrchestrationExecutor
  journal: StudioCoogoExecutionJournal
} {
  let leaseHeld = false
  const onLeaseLost = () => {
    leaseHeld = false
  }
  const journal = createStudioCoogoExecutionJournal({
    projectId: input.item.orchestrationPlan.projectId,
    runId: input.item.orchestrationPlan.runId,
    onLeaseLost,
  })
  const baseLeaseController = createStudioCoogoExecutionLeaseController({
    projectId: input.item.orchestrationPlan.projectId,
    runId: input.item.orchestrationPlan.runId,
    clientInstanceId: `studio-${stableHash(input.item.scope.sessionId)}`,
    getCheckpointRevision: () => journal.checkpointRevision,
    onLeaseLost,
  })
  const leaseController = {
    acquire: async (...args: Parameters<typeof baseLeaseController.acquire>) => {
      const loaded = await journal.load(input.item.orchestrationPlan.runId)
      if (!loaded) {
        throw new CoogoExecutionError(
          'The durable suspended checkpoint is unavailable for lease acquisition.',
          'execution_lease_lost',
        )
      }
      const lease = await baseLeaseController.acquire({
        ...args[0],
        snapshot: loaded.snapshot,
      })
      leaseHeld = true
      return lease
    },
    renew: baseLeaseController.renew.bind(baseLeaseController),
    assertCurrent: baseLeaseController.assertCurrent.bind(baseLeaseController),
    release: async (...args: Parameters<typeof baseLeaseController.release>) => {
      await baseLeaseController.release(...args)
      leaseHeld = false
    },
  }
  const handlers = createStudioOrchestrationNodeHandlers({
    bus: input.bus,
    recoveredRunningNode: () => input.recoveredRunningN4 === true,
    loadAuthenticatedN1Checkpoint: async () =>
      authenticatedN1Envelope({
        item: input.item,
        loaded: await journal.load(input.item.orchestrationPlan.runId),
      }),
    platformAuthority: {
      consumeLiveResumeAuthority: async () =>
        leaseHeld
          ? {
              accepted: true,
              actorId: input.item.scope.principalId,
              confirmationId: input.item.confirmationId,
              approvedAt: input.approvedAt,
            }
          : { accepted: false, code: 'execution_lease_required' },
    },
  })
  return {
    journal,
    executor: new CoogoOrchestrationExecutor({
      journal,
      leaseController,
      handlers,
      onEvent: () => notifyGatewayListeners(),
    }),
  }
}

function sameStep5DurableReference(
  left: StoredStep5DurableReference,
  right: StoredStep5DurableReference,
): boolean {
  return (
    left.confirmationId === right.confirmationId &&
    left.runId === right.runId &&
    left.projectId === right.projectId &&
    left.scopeKey === right.scopeKey &&
    left.createdAt === right.createdAt &&
    left.expiresAt === right.expiresAt
  )
}

function discardStep5DurableReference(
  scope: StudioCapabilityScope,
  expected: StoredStep5DurableReference,
): void {
  restoreStep5DurableReferences(scope)
  const current = step5DurableReferences.get(expected.confirmationId)
  if (!current || !sameStep5DurableReference(current, expected)) return
  step5DurableReferences.delete(expected.confirmationId)
  persistStep5DurableReferences(scope)
}

function hydratedStep5PendingPublishDecision(
  scope: StudioCapabilityScope,
  reference: StoredStep5DurableReference,
): { allowed: boolean; discardReference: boolean } {
  const currentScope = captureStudioCapabilityScope()
  if (
    !sameScope(currentScope, scope) ||
    !hasAuthenticatedDurableJournalScope(scope)
  )
    return { allowed: false, discardReference: false }
  const stored = readStoredStep5DurableReferences(scope)
  const currentReference = stored?.find(
    (candidate) => candidate.confirmationId === reference.confirmationId,
  )
  if (
    !currentReference ||
    !sameStep5DurableReference(currentReference, reference)
  )
    return { allowed: false, discardReference: false }
  const currentPending = pending.get(reference.confirmationId)
  if (currentPending) {
    return { allowed: false, discardReference: false }
  }
  const lifecycle = confirmationLifecycles.get(reference.confirmationId)
  if (
    lifecycle &&
    (lifecycle.scopeKey !== scope.key || lifecycle.status !== 'pending')
  )
    return { allowed: false, discardReference: true }
  const execution = getScopedExecutions(scope).find(
    (candidate) => candidate.orchestration.runId === reference.runId,
  )
  return execution && execution.status !== 'suspended'
    ? { allowed: false, discardReference: true }
    : { allowed: true, discardReference: false }
}

function step5PendingItemFromReadback(input: {
  confirmationId: string
  scope: StudioCapabilityScope
  reference: StoredStep5DurableReference
  readback: StudioOrchestrationRunReadback
  allowRecoveredRunningN4: boolean
}): PendingCapabilityPlan | null {
  const { confirmationId, scope, reference, readback } = input
  const readablePlan = readback.plan as unknown as CoogoReadableOrchestrationPlan
  if (
    readablePlan.schemaVersion !== 'coogo.orchestration-plan.v2' ||
    readablePlan.runId !== reference.runId ||
    readablePlan.projectId !== reference.projectId
  )
    return null
  const orchestrationPlan = readablePlan
  const gate = orchestrationPlan.nodes.find((node) => node.id === 'n2-human-gate')?.humanGate
  const mutation = orchestrationPlan.nodes.find((node) => node.id === 'n4-update-page-meta')
  const path = (
    orchestrationPlan.nodes.find((node) => node.id === 'n1-impact-before')?.operation?.params?.paths as
      | unknown[]
      | undefined
  )?.[0]
  const gateTarget = recordOf(gate?.target)
  const exactTarget = typeof path === 'string'
    ? classifyStudioExactImpactTarget(path)
    : { kind: 'unsupported' as const }
  if (
    !gate ||
    gate.confirmationId !== confirmationId ||
    typeof path !== 'string' ||
    mutation?.operation?.name !== 'cogo.studio.updatePageMeta' ||
    typeof mutation.operation.params?.pageId !== 'string' ||
    typeof mutation.operation.params?.title !== 'string' ||
    exactTarget.kind !== 'page-title' ||
    exactTarget.pageId !== mutation.operation.params.pageId ||
    gateTarget?.kind !== 'studio-page' ||
    gateTarget.projectId !== orchestrationPlan.projectId ||
    gateTarget.pageId !== mutation.operation.params.pageId
  )
    return null
  const checkpoint = readback.checkpoint
  const suspendedAtN2 =
    readback.run.status === 'suspended' &&
    checkpoint?.status === 'suspended' &&
    checkpoint.nodes['n1-impact-before']?.status === 'completed' &&
    checkpoint.nodes['n2-human-gate']?.status === 'suspended'
  const recoveredRunningN4 =
    readback.run.status === 'running' &&
    checkpoint?.status === 'running' &&
    checkpoint.nodes['n1-impact-before']?.status === 'completed' &&
    checkpoint.nodes['n2-human-gate']?.status === 'completed' &&
    checkpoint.nodes['n3-apply-grant']?.status === 'completed' &&
    checkpoint.nodes['n4-update-page-meta']?.status === 'running'
  if (!suspendedAtN2 && !(input.allowRecoveredRunningN4 && recoveredRunningN4)) {
    return null
  }
  const plan: StudioCapabilityPlan = {
    planId: orchestrationPlan.planId,
    runId: orchestrationPlan.runId,
    label: orchestrationPlan.label ?? 'Rename page',
    source: orchestrationPlan.trigger === 'webmcp' ? 'webmcp' : 'chat-operation',
    commands: [
      {
        tool: mutation.operation.name,
        params: structuredClone(mutation.operation.params),
        expectedChangedPaths: [path],
        exactChanges: [
          {
            path,
            label: 'page title',
            proposed: mutation.operation.params.title,
          },
        ],
        requiresConfirmation: true,
      },
    ],
  }
  return {
    confirmationId,
    plan,
    scope,
    createdAt: reference.createdAt,
    expiresAt: reference.expiresAt,
    impact: step5ImpactPreview(plan, gate.confirmationPhrase ?? step5ConfirmationPhrase(plan)),
    orchestrationPlan,
    events: structuredClone(readback.events),
    journalMode: 'durable',
    lifecycleOwner: 'executor-v2',
  }
}

async function hydrateStep5PendingReference(
  confirmationId: string,
  scope: StudioCapabilityScope,
  options: {
    allowRecoveredRunningN4?: boolean
    revalidateBeforePendingPublish?: boolean
  } = {},
): Promise<PendingCapabilityPlan | null> {
  restoreStep5DurableReferences(scope)
  const reference = step5DurableReferences.get(confirmationId)
  if (
    !reference ||
    reference.scopeKey !== scope.key ||
    reference.projectId !== scope.projectId ||
    reference.expiresAt <= Date.now()
  )
    return null
  const firstReadback = await readStudioOrchestrationJournal({
    projectId: reference.projectId,
    runId: reference.runId,
  })
  if (!firstReadback) return null
  const allowRecoveredRunningN4 = options.allowRecoveredRunningN4 !== false
  let item = step5PendingItemFromReadback({
    confirmationId,
    scope,
    reference,
    readback: firstReadback,
    allowRecoveredRunningN4,
  })
  if (!item) {
    if (options.revalidateBeforePendingPublish) {
      discardStep5DurableReference(scope, reference)
    }
    return null
  }
  if (options.revalidateBeforePendingPublish) {
    const beforeRead = hydratedStep5PendingPublishDecision(scope, reference)
    if (!beforeRead.allowed) {
      if (beforeRead.discardReference) {
        discardStep5DurableReference(scope, reference)
      }
      return null
    }
    const authoritativeReadback = await readStudioOrchestrationJournal({
      projectId: reference.projectId,
      runId: reference.runId,
    })
    if (!authoritativeReadback) return null
    item = step5PendingItemFromReadback({
      confirmationId,
      scope,
      reference,
      readback: authoritativeReadback,
      allowRecoveredRunningN4: false,
    })
    if (!item) {
      discardStep5DurableReference(scope, reference)
      return null
    }
    const beforePublish = hydratedStep5PendingPublishDecision(scope, reference)
    if (!beforePublish.allowed) {
      if (beforePublish.discardReference) {
        discardStep5DurableReference(scope, reference)
      }
      return null
    }
  }
  pending.set(confirmationId, item)
  return item
}

function hydrateStep5PendingReferencesForList(scope: StudioCapabilityScope): void {
  restoreStep5DurableReferences(scope)
  if (!hasAuthenticatedDurableJournalScope(scope)) return
  const references = [...step5DurableReferences.values()].filter(
    (reference) =>
      reference.scopeKey === scope.key &&
      reference.projectId === scope.projectId &&
      reference.expiresAt > Date.now() &&
      !pending.has(reference.confirmationId),
  )
  if (references.length === 0 || step5PendingHydrations.has(scope.key)) return
  const hydration = Promise.all(
    references.map(async (reference) => {
      try {
        return await hydrateStep5PendingReference(reference.confirmationId, scope, {
          allowRecoveredRunningN4: false,
          revalidateBeforePendingPublish: true,
        })
      } catch {
        return null
      }
    }),
  )
    .then((items) => {
      if (items.some((item) => item !== null)) notifyGatewayListeners()
    })
    .finally(() => {
      if (step5PendingHydrations.get(scope.key) === hydration) {
        step5PendingHydrations.delete(scope.key)
      }
    })
  step5PendingHydrations.set(scope.key, hydration)
}

async function appendAndJournalStudioOrchestrationEvent(input: {
  events: CoogoExecutionEvent[]
  plan: CoogoOrchestrationPlan
  durableJournal: boolean
  type: Parameters<typeof appendStudioOrchestrationEvent>[0]['type']
  nodeId?: string
  data?: Record<string, unknown>
}): Promise<CoogoExecutionEvent> {
  const event = appendStudioOrchestrationEvent({
    ...input,
    ...(input.data ? { data: sanitize(input.data) as Record<string, unknown> } : {}),
  })
  if (!input.durableJournal) return event
  try {
    const mode = await appendStudioOrchestrationJournalEvent(event)
    if (mode !== 'durable') {
      throw new Error('Authenticated orchestration journal access is required to continue this durable run.')
    }
  } catch (writeError) {
    try {
      const readback = await readStudioOrchestrationJournal({
        projectId: event.projectId,
        runId: event.runId,
      })
      const persisted = readback?.events.find((candidate) => candidate.sequence === event.sequence)
      if (persisted?.eventId === event.eventId) return event
    } catch {
      // Preserve the original write failure; the caller performs safe recovery.
    }
    const localIndex = input.events.findIndex((candidate) => candidate.eventId === event.eventId)
    if (localIndex >= 0) input.events.splice(localIndex, 1)
    throw writeError
  }
  return event
}

async function executeConfirmedPlan(
  plan: StudioCapabilityPlan,
  bus: StudioMcpBus,
  scope: StudioCapabilityScope,
  orchestrationPlan: CoogoOrchestrationPlan,
  initialEvents: CoogoExecutionEvent[],
  durableJournal: boolean,
  approval: StudioCapabilityExecution['approval'],
): Promise<StudioCapabilityExecutionResult> {
  const errors = preflightPlan(bus, plan)
  if (errors.length) return { status: 'failed', error: errors.join(' ') }
  const before = captureStudioCapabilityState()
  const results: StudioCapabilityExecution['results'] = []
  const events = structuredClone(initialEvents)
  const finalizeFailedExecution = async (input: {
    error: string
    failedTool: string
    nodeId?: string
    appendFailureEvents?: boolean
    abortDurableRun?: boolean
    // wave-3 (Lane A) — carries a StudioCapabilityFailureCode through to the
    // returned result's `code` field (e.g. CONFIRMATION_STALE_AT_COMMIT),
    // same slot CONFIRMATION_STALE/CONFIRMATION_IN_FLIGHT already use, so
    // the existing display-boundary resolution in studio-task-center.tsx
    // picks it up unmodified.
    code?: StudioCapabilityFailureCode
  }): Promise<StudioCapabilityExecutionResult> => {
    const journalErrors: string[] = []
    let durableAbortConfirmed = !input.abortDurableRun || !durableJournal
    let durableAbortError: string | undefined
    if (input.abortDurableRun && durableJournal) {
      try {
        const mode = await cancelStudioOrchestrationJournal({
          projectId: orchestrationPlan.projectId,
          runId: orchestrationPlan.runId,
          reason: 'journal-ack-failure',
        })
        durableAbortConfirmed = mode === 'durable'
        if (!durableAbortConfirmed) {
          durableAbortError =
            'The durable run could not be terminated because authenticated journal access is unavailable.'
        }
      } catch (error) {
        durableAbortError = error instanceof Error ? error.message : String(error)
        journalErrors.push(`Durable run termination is unconfirmed: ${durableAbortError}`)
      }
    }
    if (input.appendFailureEvents !== false && input.nodeId) {
      try {
        await appendAndJournalStudioOrchestrationEvent({
          events,
          plan: orchestrationPlan,
          durableJournal,
          type: 'node.failed',
          nodeId: input.nodeId,
          data: { tool: input.failedTool, error: input.error },
        })
      } catch (error) {
        journalErrors.push(error instanceof Error ? error.message : String(error))
      }
    }

    const failedState = captureStudioCapabilityState()
    const undoState = getUndoState()
    const hasUndoEntry = undoState.topCapabilityPlanId === plan.planId
    let rollbackAttempted = false
    let rollbackCommandSucceeded = false
    let rollbackCommandError: string | undefined
    if (durableAbortConfirmed && hasUndoEntry && bus.hasTool('cogo.studio.undo')) {
      rollbackAttempted = true
      try {
        const rollbackResult = await bus.execute({
          tool: 'cogo.studio.undo',
          params: { _discardRedo: true },
        })
        rollbackCommandSucceeded = rollbackResult.success
        rollbackCommandError = rollbackResult.success
          ? undefined
          : (rollbackResult.error ?? 'Automatic rollback failed.')
      } catch (error) {
        rollbackCommandError = error instanceof Error ? error.message : String(error)
      }
    }
    const after = captureStudioCapabilityState()
    const stateRestored = after.fingerprint === before.fingerprint
    const rollback: NonNullable<StudioCapabilityExecution['rollback']> = {
      attempted: rollbackAttempted,
      succeeded: stateRestored,
      ...(!stateRestored
        ? {
            error:
              rollbackCommandError ??
              (!durableAbortConfirmed
                ? 'Studio retained the current state because durable run termination was not confirmed. Reconcile the journal before undoing or retrying.'
                : undefined) ??
              (hasUndoEntry && !bus.hasTool('cogo.studio.undo')
                ? 'Automatic rollback is unavailable on this MCP bus, and the current state differs from the pre-run readback.'
                : rollbackAttempted && rollbackCommandSucceeded
                  ? 'The undo command returned success, but the authoritative state fingerprint was not restored.'
                  : failedState.fingerprint !== before.fingerprint
                    ? 'The failed operation retained a state change and no verified rollback completed.'
                    : 'Studio could not verify that the pre-run state was restored.'),
          }
        : {}),
    }
    if (input.appendFailureEvents !== false && input.nodeId) {
      try {
        await appendAndJournalStudioOrchestrationEvent({
          events,
          plan: orchestrationPlan,
          durableJournal,
          type: 'plan.failed',
          data: {
            beforeFingerprint: before.fingerprint,
            afterFingerprint: after.fingerprint,
            rollbackAttempted,
            rollbackSucceeded: stateRestored,
          },
        })
      } catch (error) {
        journalErrors.push(error instanceof Error ? error.message : String(error))
      }
    }
    const error = [
      input.error,
      journalErrors.length > 0 ? `Execution journal is incomplete: ${journalErrors.join(' ')}` : null,
      !rollback.succeeded ? rollback.error : null,
    ]
      .filter(Boolean)
      .join(' ')
    const execution: StudioCapabilityExecution = {
      executionId: crypto.randomUUID(),
      planId: plan.planId,
      label: plan.label,
      source: plan.source,
      projectId: scope.projectId,
      sessionId: scope.sessionId,
      status: 'failed',
      tools: plan.commands.map((item) => item.tool),
      changedPaths: changedPaths(plan),
      before,
      after,
      results: [
        ...results,
        ...(!results.some((entry) => entry.tool === input.failedTool && !entry.success)
          ? [{ tool: input.failedTool, success: false, error: input.error }]
          : []),
      ],
      undoAvailable: getUndoState().topCapabilityPlanId === plan.planId,
      rollback,
      approval,
      createdAt: new Date().toISOString(),
      orchestration: studioOrchestrationReference(orchestrationPlan),
      events,
      error,
    }
    appendExecution(scope, execution)
    return { status: 'failed', execution, error, ...(input.code ? { code: input.code } : {}) }
  }
  if (approval.required) {
    try {
      await appendAndJournalStudioOrchestrationEvent({
        events,
        plan: orchestrationPlan,
        durableJournal,
        type: 'plan.resumed',
        data: { approvalSource: approval.source },
      })
    } catch (error) {
      return finalizeFailedExecution({
        error: `The durable run could not resume before execution: ${
          error instanceof Error ? error.message : String(error)
        }`,
        failedTool: 'orchestration.journal',
        appendFailureEvents: false,
        abortDurableRun: true,
      })
    }
  }
  const mutatingIndexes = plan.commands
    .map((command, index) => (isMutatingCommand(bus, command) ? index : -1))
    .filter((index) => index >= 0)
  const firstMutation = mutatingIndexes[0]
  const lastMutation = mutatingIndexes[mutatingIndexes.length - 1]

  for (let index = 0; index < plan.commands.length; index += 1) {
    const command = plan.commands[index]
    const orchestrationNode = orchestrationPlan.nodes[index]
    try {
      await appendAndJournalStudioOrchestrationEvent({
        events,
        plan: orchestrationPlan,
        durableJournal,
        type: 'node.started',
        nodeId: orchestrationNode.id,
        data: { tool: command.tool },
      })
    } catch (error) {
      return finalizeFailedExecution({
        error: `The durable node start was not acknowledged before execution: ${
          error instanceof Error ? error.message : String(error)
        }`,
        failedTool: 'orchestration.journal',
        nodeId: orchestrationNode.id,
        appendFailureEvents: false,
        abortDurableRun: true,
      })
    }
    const mutating = mutatingIndexes.includes(index)
    const params: Record<string, unknown> = {
      ...command.params,
      ...(mutating ? { _capabilityPlanId: plan.planId } : {}),
      ...(mutatingIndexes.length > 1 && index === firstMutation ? { _transactionStart: true } : {}),
      ...(mutatingIndexes.length > 1 && index === lastMutation ? { _transactionEnd: true } : {}),
      // wave-3 (Lane A) — threads the plan-time before-snapshot into the
      // command params so a tool handler can re-verify it against the live
      // store as the LAST step before its write (closing cell (b); see
      // exactChangesDriftFromLiveState() and its call site in
      // register-studio-tools.ts's `updatePageMeta` handler). Inert for any
      // handler that does not read it — same additive convention as
      // _capabilityPlanId above.
      ...(command.exactChanges?.length ? { _expectedBeforeSnapshot: command.exactChanges } : {}),
    }
    let approvalToken: string | undefined
    if (
      approval.required &&
      approval.confirmationId &&
      approval.approvedAt &&
      requiresCapabilityApprovalGrant(bus, command)
    ) {
      approvalToken = issueStudioCapabilityApprovalGrant({
        tool: command.tool,
        params,
        projectId: scope.projectId,
        actorId: useStudioStore.getState().user?.id ?? 'studio-local-user',
        confirmationId: approval.confirmationId,
        approvedAt: approval.approvedAt,
      })
      params._capabilityApprovalToken = approvalToken
    }
    let result: Awaited<ReturnType<StudioMcpBus['executeWebMcp']>>
    try {
      result = await bus.executeWebMcp({ tool: command.tool, params })
    } catch (error) {
      return finalizeFailedExecution({
        error: `Studio tool middleware threw before a result was returned: ${
          error instanceof Error ? error.message : String(error)
        }`,
        failedTool: command.tool,
        nodeId: orchestrationNode.id,
      })
    } finally {
      if (approvalToken) {
        try {
          consumeStudioCapabilityApprovalGrant({
            token: approvalToken,
            tool: command.tool,
            params,
            projectId: scope.projectId,
            failureScope: 'studio',
          })
        } catch {
          // The tool boundary or handler already consumed the one-time grant.
        }
      }
    }
    if (!result.success) {
      // wave-3 (Lane A) — a commit-time drift throw from the writer's tool
      // handler (exactChangesDriftFromLiveState) reaches here as a plain
      // McpResult failure with the marker prefix on `error`, since
      // studio-mcp-bus.ts's handler catch only forwards Error.message. See
      // CONFIRMATION_STALE_AT_COMMIT_MARKER.
      const staleAtCommit = result.error?.startsWith(CONFIRMATION_STALE_AT_COMMIT_MARKER)
      return finalizeFailedExecution({
        error: staleAtCommit
          ? result.error!.slice(CONFIRMATION_STALE_AT_COMMIT_MARKER.length)
          : (result.error ?? `${command.tool} failed.`),
        failedTool: command.tool,
        nodeId: orchestrationNode.id,
        ...(staleAtCommit ? { code: 'CONFIRMATION_STALE_AT_COMMIT' as const } : {}),
      })
    }
    let readback: StudioCapabilityExecution['results'][number]['readback']
    if (command.readback) {
      let readbackResult: Awaited<ReturnType<StudioMcpBus['execute']>>
      try {
        readbackResult = await bus.execute({
          tool: command.readback.tool,
          params: command.readback.params ?? {},
        })
      } catch (error) {
        return finalizeFailedExecution({
          error: `Studio readback middleware threw before a result was returned: ${
            error instanceof Error ? error.message : String(error)
          }`,
          failedTool: command.readback.tool,
          nodeId: orchestrationNode.id,
        })
      }
      readback = readbackResult.success
        ? { status: 'success', data: sanitize(readbackResult.data) }
        : {
            status: 'failed',
            error: readbackResult.error ?? 'Readback failed.',
          }
    }
    results.push({
      tool: command.tool,
      success: true,
      data: sanitize(result.data),
      ...(readback !== undefined ? { readback } : {}),
    })
    try {
      await appendAndJournalStudioOrchestrationEvent({
        events,
        plan: orchestrationPlan,
        durableJournal,
        type: 'node.completed',
        nodeId: orchestrationNode.id,
        data: { tool: command.tool, readbackStatus: readback?.status ?? 'not-requested' },
      })
    } catch (error) {
      return finalizeFailedExecution({
        error: `The tool executed, but its durable journal acknowledgement failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
        failedTool: command.tool,
        nodeId: orchestrationNode.id,
        appendFailureEvents: false,
        abortDurableRun: true,
      })
    }
  }

  const after = captureStudioCapabilityState()
  try {
    await appendAndJournalStudioOrchestrationEvent({
      events,
      plan: orchestrationPlan,
      durableJournal,
      type: 'plan.completed',
      data: {
        beforeFingerprint: before.fingerprint,
        afterFingerprint: after.fingerprint,
      },
    })
  } catch (error) {
    return finalizeFailedExecution({
      error: `The plan executed, but its durable completion acknowledgement failed: ${
        error instanceof Error ? error.message : String(error)
      }`,
      failedTool: 'orchestration.journal',
      appendFailureEvents: false,
      abortDurableRun: true,
    })
  }
  const undoState = getUndoState()
  const execution: StudioCapabilityExecution = {
    executionId: crypto.randomUUID(),
    planId: plan.planId,
    label: plan.label,
    source: plan.source,
    projectId: scope.projectId,
    sessionId: scope.sessionId,
    status: 'executed',
    tools: plan.commands.map((item) => item.tool),
    changedPaths: changedPaths(plan),
    before,
    after,
    results,
    undoAvailable: undoState.topCapabilityPlanId === plan.planId,
    approval,
    createdAt: new Date().toISOString(),
    orchestration: studioOrchestrationReference(orchestrationPlan),
    events,
  }
  appendExecution(scope, execution)
  return { status: 'executed', execution }
}

async function executeAuthenticatedStep5PageRename(input: {
  plan: StudioCapabilityPlan
  bus: StudioMcpBus
  scope: StudioCapabilityScope
}): Promise<StudioCapabilityGatewayResult> {
  await cleanExpiredPending({ reserveSlot: true })
  const confirmationId = crypto.randomUUID()
  const createdAt = Date.now()
  const expiresAt = createdAt + PENDING_TTL_MS
  const confirmationPhrase = step5ConfirmationPhrase(input.plan)
  const impact = step5ImpactPreview(input.plan, confirmationPhrase)
  let orchestrationPlan: CoogoOrchestrationPlanV2
  try {
    orchestrationPlan = compileStudioOrchestrationPlan({
      mode: 'step5-page-title-rename',
      plan: input.plan,
      projectId: orchestrationProjectId(input.scope),
      isReadOnly: (command) => !isMutatingCommand(input.bus, command),
      admission: {
        authenticated: true,
        durability: 'durable',
        confirmationId,
        confirmationPhrase,
        expiresAt: new Date(expiresAt).toISOString(),
      },
      createdAt: new Date(createdAt).toISOString(),
    })
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
    }
  }
  try {
    if ((await createStudioOrchestrationJournal(orchestrationPlan)) !== 'durable') {
      return {
        status: 'failed',
        code: 'DURABLE_JOURNAL_REQUIRED',
        error: 'Authenticated orchestration journal access is required for Step 5.',
      }
    }
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
    }
  }
  const item: PendingCapabilityPlan = {
    confirmationId,
    plan: structuredClone(input.plan),
    scope: input.scope,
    createdAt,
    expiresAt,
    impact,
    orchestrationPlan,
    events: [],
    journalMode: 'durable',
    lifecycleOwner: 'executor-v2',
  }
  const { executor } = createStep5Executor({
    item,
    bus: input.bus,
    approvalSource: 'studio-user-action',
    approvedAt: new Date(createdAt).toISOString(),
  })
  try {
    activeStep5Executors.set(orchestrationPlan.runId, executor)
    const result = await executor.execute(orchestrationPlan)
    if (result.status !== 'suspended' || result.suspension?.nodeId !== 'n2-human-gate') {
      const failure = executorError(result)
      return {
        status: 'failed',
        ...(failure.code ? { executionFailureCode: failure.code } : {}),
        error: failure.message,
      }
    }
    item.events = structuredClone(result.events)
    pending.set(confirmationId, item)
    step5DurableReferences.set(confirmationId, {
      confirmationId,
      runId: orchestrationPlan.runId,
      projectId: orchestrationPlan.projectId,
      scopeKey: input.scope.key,
      createdAt,
      expiresAt,
    })
    persistStep5DurableReferences(input.scope)
    recordConfirmationLifecycle(input.scope, {
      confirmationId,
      status: 'pending',
    })
    const execution = projectStep5Execution({
      item,
      result,
      approvalSource: 'awaiting-user-approval',
    })
    upsertStep5Execution(input.scope, execution)
    return {
      status: 'confirmation-required',
      confirmationId,
      planId: input.plan.planId,
      label: input.plan.label,
      tools: input.plan.commands.map((command) => command.tool),
      changedPaths: changedPaths(input.plan),
      projectId: input.scope.projectId,
      expiresAt: new Date(expiresAt).toISOString(),
      impact,
      orchestration: step5OrchestrationReference(orchestrationPlan),
      lifecycle: {
        runId: result.runId,
        nodeId: result.suspension.nodeId,
        status: 'suspended',
      },
    }
  } catch (error) {
    // task-1 — pass through any CoogoExecutionError code (previously only
    // 'execution_lease_lost'); see the StudioCapabilityExecutionFailureCode
    // comment above. fix-round-1 (I3): validated, not cast.
    return {
      status: 'failed',
      ...(error instanceof CoogoExecutionError
        ? { executionFailureCode: narrowExecutionFailureCode(error.code) }
        : {}),
      error: error instanceof Error ? error.message : String(error),
    }
  } finally {
    activeStep5Executors.delete(orchestrationPlan.runId)
  }
}

export async function executeStudioCapabilityPlan(
  inputPlan: StudioCapabilityPlan,
  options: { bus?: StudioMcpBus } = {},
): Promise<StudioCapabilityGatewayResult> {
  const bus = options.bus ?? mcpBus
  let plan: StudioCapabilityPlan
  try {
    plan = prepareExactImpactPlan(inputPlan, bus)
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
    }
  }
  const scope = captureStudioCapabilityScope()
  const errors = preflightPlan(bus, plan)
  if (errors.length) return { status: 'failed', error: errors.join(' ') }
  if (isAuthenticatedStep5PageRename(plan, bus, scope)) {
    return executeAuthenticatedStep5PageRename({ plan, bus, scope })
  }
  let orchestrationPlan: CoogoOrchestrationPlan
  try {
    orchestrationPlan = compileStudioOrchestrationPlan({
      plan,
      projectId: orchestrationProjectId(scope),
      isReadOnly: (command) => !isMutatingCommand(bus, command),
    })
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
    }
  }
  const events: CoogoExecutionEvent[] = []
  let durableJournal = false
  try {
    if (canPersistPlan(plan) && hasAuthenticatedDurableJournalScope(scope)) {
      durableJournal = (await createStudioOrchestrationJournal(orchestrationPlan)) === 'durable'
    }
    await appendAndJournalStudioOrchestrationEvent({
      events,
      plan: orchestrationPlan,
      durableJournal,
      type: 'plan.accepted',
    })
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
    }
  }
  const needsConfirmation = plan.commands.some((command) => isMutatingCommand(bus, command))
  if (needsConfirmation) {
    await cleanExpiredPending({ reserveSlot: true })
    const confirmationId = crypto.randomUUID()
    const createdAt = Date.now()
    const impact = buildImpactPreview(plan)
    try {
      await appendAndJournalStudioOrchestrationEvent({
        events,
        plan: orchestrationPlan,
        durableJournal,
        type: 'plan.suspended',
        data: { reason: 'confirmation-required' },
      })
    } catch (error) {
      return {
        status: 'failed',
        error: error instanceof Error ? error.message : String(error),
      }
    }
    pending.set(confirmationId, {
      confirmationId,
      plan: structuredClone(plan),
      scope,
      createdAt,
      expiresAt: createdAt + PENDING_TTL_MS,
      impact,
      orchestrationPlan,
      events,
      journalMode: durableJournal ? 'durable' : 'session-only',
    })
    recordConfirmationLifecycle(scope, {
      confirmationId,
      status: 'pending',
    })
    persistPendingCapabilities(scope)
    notifyGatewayListeners()
    return {
      status: 'confirmation-required',
      confirmationId,
      planId: plan.planId,
      label: plan.label,
      tools: plan.commands.map((command) => command.tool),
      changedPaths: changedPaths(plan),
      projectId: scope.projectId,
      expiresAt: new Date(createdAt + PENDING_TTL_MS).toISOString(),
      impact,
      orchestration: studioOrchestrationReference(orchestrationPlan),
    }
  }
  try {
    return await executeConfirmedPlan(
      plan,
      bus,
      scope,
      orchestrationPlan,
      events,
      durableJournal,
      {
        required: false,
        source: 'not-required',
      },
    )
  } catch (error) {
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

function legacyWriterEvents(
  events: CoogoReadableExecutionEvent[],
): CoogoExecutionEvent[] {
  if (
    !events.every(
      (event): event is CoogoExecutionEvent =>
        event.schemaVersion === 'coogo.execution-event.v1',
    )
  ) {
    throw new Error(
      'The legacy Studio execution writer cannot continue from v2 events.',
    )
  }
  return events
}

function stableRecoveryStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableRecoveryStringify(item)).join(',')}]`
  }
  if (value && typeof value === 'object') {
    const object = value as Record<string, unknown>
    return `{${Object.keys(object)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableRecoveryStringify(object[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

function recoveredStep5PlanFingerprint(plan: CoogoOrchestrationPlanV2): string {
  const source = stableRecoveryStringify({ plan, outputNodeIds: null })
  let hash = 0x811c9dc5
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`
}

function recoveredStep5MutationAuthority(input: {
  item: PendingCapabilityPlan
  snapshot: CoogoExecutionSnapshot
}): { path: string; proposedValue: string } {
  const { item, snapshot } = input
  const plan = item.orchestrationPlan as CoogoOrchestrationPlanV2
  const beforeNode = plan.nodes.find((node) => node.id === 'n1-impact-before')
  const gateNode = plan.nodes.find((node) => node.id === 'n2-human-gate')
  const mutationNode = plan.nodes.find((node) => node.id === 'n4-update-page-meta')
  const gate = gateNode?.humanGate
  const gateTarget = recordOf(gate?.target)
  const pageId = mutationNode?.operation?.params?.pageId
  const proposedValue = mutationNode?.operation?.params?.title
  const path = Array.isArray(beforeNode?.operation?.params?.paths)
    ? beforeNode.operation.params.paths[0]
    : undefined
  const expectedPath = typeof pageId === 'string'
    ? `/manifest/pages/${encodeURIComponent(pageId)}/title`
    : null
  const durableBefore = snapshot.nodes['n1-impact-before']?.output as
    | CoogoCheckpointExactImpactOutputV1
    | undefined
  const durableObservation = durableBefore?.kind === 'exact-impact'
    ? durableBefore.observations[0]
    : undefined
  const expectedAdmissionBinding = gate
    ? {
        gateNodeId: gateNode.id,
        confirmationId: gate.confirmationId,
        expiresAt: gate.expiresAt,
        principalBinding: gate.principalBinding,
        sessionBinding: gate.sessionBinding,
        target: structuredClone(gate.target),
        duplicatePolicy: gate.duplicatePolicy,
        durability: gate.durability,
      }
    : undefined
  if (
    plan.schemaVersion !== 'coogo.orchestration-plan.v2' ||
    snapshot.planId !== plan.planId ||
    snapshot.runId !== plan.runId ||
    snapshot.projectId !== plan.projectId ||
    snapshot.planFingerprint !== recoveredStep5PlanFingerprint(plan) ||
    snapshot.outputNodeIds !== null ||
    stableRecoveryStringify(snapshot.admissionBinding) !==
      stableRecoveryStringify(expectedAdmissionBinding) ||
    gate?.durability !== 'durable' ||
    snapshot.nodes['n4-update-page-meta']?.status !== 'running' ||
    gateTarget?.kind !== 'studio-page' ||
    gateTarget.projectId !== plan.projectId ||
    gateTarget.pageId !== pageId ||
    typeof pageId !== 'string' ||
    typeof proposedValue !== 'string' ||
    typeof path !== 'string' ||
    path !== expectedPath ||
    durableBefore?.kind !== 'exact-impact' ||
    durableBefore.mode !== 'capture' ||
    durableBefore.verified !== true ||
    durableObservation?.path !== path
  ) {
    throw new CoogoExecutionError(
      'Recovered Step 5 mutation authority does not match the admitted target.',
      'checkpoint_authority_mismatch',
    )
  }
  const target = classifyStudioExactImpactTarget(path)
  if (target.kind !== 'page-title' || target.pageId !== pageId) {
    throw new CoogoExecutionError(
      'Recovered Step 5 mutation path is not the admitted page title.',
      'checkpoint_authority_mismatch',
    )
  }
  return { path, proposedValue }
}

function recoveredCheckpointFromSnapshot(
  snapshot: CoogoExecutionSnapshot,
): CoogoExecutionCheckpointV1 {
  const n1 = snapshot.nodes['n1-impact-before']
  const n2 = snapshot.nodes['n2-human-gate']
  const n4 = snapshot.nodes['n4-update-page-meta']
  return {
    schemaVersion: COOGO_EXECUTION_CHECKPOINT_V1_VERSION,
    planId: snapshot.planId,
    runId: snapshot.runId,
    projectId: snapshot.projectId,
    planFingerprint: snapshot.planFingerprint,
    outputNodeIds: structuredClone(snapshot.outputNodeIds),
    ...(snapshot.admissionBinding
      ? { admissionBinding: structuredClone(snapshot.admissionBinding) }
      : {}),
    status: snapshot.status,
    sequence: snapshot.sequence,
    nodes: {
      ...(n1
        ? {
            'n1-impact-before': {
              nodeId: n1.nodeId,
              status: n1.status,
              ...(n1.output ? { checkpointOutput: structuredClone(n1.output) as never } : {}),
            },
          }
        : {}),
      ...(n2
        ? {
            'n2-human-gate': {
              nodeId: n2.nodeId,
              status: n2.status,
              ...(n2.suspension
                ? { suspension: structuredClone(n2.suspension) as never }
                : {}),
            },
          }
        : {}),
      ...(n4
        ? {
            'n4-update-page-meta': {
              nodeId: n4.nodeId,
              status: n4.status,
            },
          }
        : {}),
    },
    updatedAt: snapshot.updatedAt,
  }
}

async function readRecoveredStep5MutationOutcome(input: {
  item: PendingCapabilityPlan
  snapshot: CoogoExecutionSnapshot
  bus: StudioMcpBus
}): Promise<StudioCapabilityMutationRecoveryReceipt> {
  const authority = recoveredStep5MutationAuthority(input)
  const result = await input.bus.execute({
    tool: 'cogo.studio.capability.readbackExactImpact',
    params: { mode: 'capture', paths: [authority.path] },
  })
  const data = recordOf(result.data)
  const observations = Array.isArray(data?.observations) ? data.observations : []
  const rawObservation = recordOf(observations[0])
  if (
    !result.success ||
    data?.verified !== true ||
    observations.length !== 1 ||
    rawObservation?.path !== authority.path ||
    rawObservation.presence !== true ||
    typeof rawObservation.value !== 'string'
  ) {
    throw new CoogoExecutionError(
      'Recovered Step 5 exact-impact readback is unavailable or malformed.',
      'exact_impact_unavailable',
    )
  }
  const observation: CoogoCheckpointObservationV1 = {
    path: authority.path,
    presence: 'present',
    valueKind: 'json-primitive',
    value: rawObservation.value,
  }
  const classified = classifyStudioCoogoUnknownMutationOutcome({
    checkpoint: recoveredCheckpointFromSnapshot(input.snapshot),
    proposedValue: authority.proposedValue,
    observation,
  })
  const guidance: StudioCapabilityMutationRecoveryGuidance =
    classified.observedOutcome === 'exact-before'
      ? 'fresh-fence-and-durable-receipt-required-before-replay'
      : classified.observedOutcome === 'exact-proposed'
        ? 'original-durable-receipt-required-before-completion'
        : 'manual-reconciliation-required'
  return {
    schemaVersion: 'studio.mutation-recovery.v1',
    ...classified,
    guidance,
  }
}

async function confirmAuthenticatedStep5PageRename(input: {
  item: PendingCapabilityPlan
  bus: StudioMcpBus
  source: StudioCapabilityApprovalSource
}): Promise<StudioCapabilityGatewayResult> {
  const { item } = input
  const approvedAt = new Date().toISOString()
  const probe = createStep5Executor({
    item,
    bus: input.bus,
    approvalSource: input.source,
    approvedAt,
  })
  try {
    const loaded = await probe.journal.load(item.orchestrationPlan.runId)
    if (!loaded) {
      return {
        status: 'failed',
        error: 'The durable Step 5 execution checkpoint is unavailable.',
      }
    }
    const recoveryCheckpoint = recoveredCheckpointFromSnapshot(loaded.snapshot)
    const recovery = classifyStudioCoogoExecutionRecovery(recoveryCheckpoint)
    if (recovery.kind === 'mutation_outcome_unknown') {
      const mutationRecovery = await readRecoveredStep5MutationOutcome({
        item,
        snapshot: loaded.snapshot,
        bus: input.bus,
      })
      const execution: StudioCapabilityExecution = {
        ...projectStep5Execution({
          item,
          result: {
            planId: loaded.snapshot.planId,
            runId: loaded.snapshot.runId,
            projectId: loaded.snapshot.projectId,
            status: 'failed',
            outputs: {},
            nodes: loaded.snapshot.nodes,
            events: loaded.snapshot.events,
      },
          approvalSource: input.source,
          approvedAt,
        }),
        status: 'failed',
        undoAvailable: false,
        results: [],
        recovery: mutationRecovery,
        error: 'The page mutation outcome is unknown and requires manual reconciliation.',
      }
      // fix-round-1 (I2) — assigned as its own statement, not as a key
      // inside the object literal above, so this can never depend on where
      // the ...projectStep5Execution(...) spread sits relative to it (that
      // spread's own executorError()-derived executionFailureCode, if any,
      // reflects unrelated recovered node state and must never override
      // this branch's actual classification). This N4-recovery path is
      // unconditionally 'mutation_outcome_unknown' —
      // classifyStudioCoogoExecutionRecovery already gated on that above.
      execution.executionFailureCode = 'mutation_outcome_unknown'
      upsertStep5Execution(item.scope, execution)
      recordConfirmationLifecycle(item.scope, {
        confirmationId: item.confirmationId,
        status: 'failed',
        error: execution.error ?? 'The page mutation outcome is unknown.',
        executionId: execution.executionId,
      })
      pending.delete(item.confirmationId)
      step5DurableReferences.delete(item.confirmationId)
      persistStep5DurableReferences(item.scope)
      return {
        status: 'failed',
        executionFailureCode: 'mutation_outcome_unknown',
        execution,
        recovery: mutationRecovery,
        error: execution.error ?? 'The page mutation outcome is unknown.',
      }
    }
    if (recovery.kind !== 'automatic-resume') {
      return {
        status: 'failed',
        error: `The durable Step 5 run is not resumable (${recovery.kind}).`,
      }
    }

    recordConfirmationLifecycle(item.scope, {
      confirmationId: item.confirmationId,
      status: 'executing',
    })
    activeStep5Executors.set(item.orchestrationPlan.runId, probe.executor)
    const result = await probe.executor.execute(item.orchestrationPlan, {
      resume: true,
      resumeData: {
        'n2-human-gate': {
          confirmationId: item.confirmationId,
          confirmationPhrase: item.impact.confirmationPhrase,
          target: structuredClone(
            (item.orchestrationPlan as CoogoOrchestrationPlanV2).nodes.find((node) => node.id === 'n2-human-gate')
              ?.humanGate?.target,
          ),
        },
      },
    })
    const execution = projectStep5Execution({
      item,
      result,
      approvalSource: input.source,
      approvedAt,
    })
    upsertStep5Execution(item.scope, execution)
    pending.delete(item.confirmationId)
    step5DurableReferences.delete(item.confirmationId)
    persistStep5DurableReferences(item.scope)
    const succeeded = result.status === 'completed'
    const failure = succeeded ? null : executorError(result)
    recordConfirmationLifecycle(item.scope, {
      confirmationId: item.confirmationId,
      status: succeeded ? 'executed' : 'failed',
      executionId: execution.executionId,
      ...(failure ? { error: failure.message } : {}),
    })
    return succeeded
      ? { status: 'executed', execution }
      : {
          status: 'failed',
          execution,
          ...(failure?.code ? { executionFailureCode: failure.code } : {}),
          error: failure?.message ?? 'The Studio orchestration failed.',
        }
  } catch (error) {
    // task-1 — pass through any CoogoExecutionError code (previously only
    // 'execution_lease_lost'); see the StudioCapabilityExecutionFailureCode
    // comment above. fix-round-1 (I3): validated, not cast.
    const code =
      error instanceof CoogoExecutionError
        ? narrowExecutionFailureCode(error.code)
        : undefined
    recordConfirmationLifecycle(item.scope, {
      confirmationId: item.confirmationId,
      status: 'failed',
      error: error instanceof Error ? error.message : String(error),
    })
    return {
      status: 'failed',
      ...(code ? { executionFailureCode: code } : {}),
      error: error instanceof Error ? error.message : String(error),
    }
  } finally {
    activeStep5Executors.delete(item.orchestrationPlan.runId)
    notifyGatewayListeners()
  }
}

async function confirmStudioCapabilityWithSource(
  confirmationId: string,
  bus: StudioMcpBus,
  source: StudioCapabilityApprovalSource,
  confirmationText?: string,
): Promise<StudioCapabilityGatewayResult> {
  await cleanExpiredPending()
  const scope = captureStudioCapabilityScope()
  restorePendingCapabilities(scope)
  let item = pending.get(confirmationId)
  if (!item) {
    item = (await hydrateStep5PendingReference(confirmationId, scope)) ?? undefined
  }
  if (!item)
    return {
      status: 'failed',
      code: 'CONFIRMATION_MISSING_OR_EXPIRED',
      error: 'Confirmation is missing or expired.',
    }
  if (!sameScope(item.scope, scope)) {
    pending.delete(confirmationId)
    persistPendingCapabilities(item.scope)
    return {
      status: 'failed',
      code: 'CONFIRMATION_SCOPE_MISMATCH',
      error: 'Confirmation belongs to a different Studio project or session.',
    }
  }
  if (
    item.impact.confirmation === 'typed' &&
    confirmationText?.trim() !== item.impact.confirmationPhrase
  ) {
    return {
      status: 'failed',
      code: 'CONFIRMATION_PHRASE_MISMATCH',
      error: `Type ${JSON.stringify(item.impact.confirmationPhrase)} to confirm this ${item.impact.risk} operation.`,
    }
  }
  if (item.lifecycleOwner === 'executor-v2') {
    return confirmAuthenticatedStep5PageRename({ item, bus, source })
  }
  const changedPreview =
    validateDesignMutationConfirmationPreview(item.plan) ??
    validateCoreSurfaceConfirmationPreview(item.plan) ??
    validateProjectMutationConfirmationPreview(item.plan) ??
    validateDeclaredOperationConfirmationPreview(item.plan, bus)
  if (changedPreview) {
    pending.delete(confirmationId)
    const error = `${changedPreview} Request a fresh proposal and confirm it again.`
    recordConfirmationLifecycle(item.scope, {
      confirmationId,
      status: 'failed',
      failureCode: 'CONFIRMATION_STALE',
      error,
    })
    persistPendingCapabilities(item.scope)
    notifyGatewayListeners()
    return {
      status: 'failed',
      code: 'CONFIRMATION_STALE',
      error,
    }
  }
  if (confirmationsInFlight.has(confirmationId)) {
    return {
      status: 'failed',
      code: 'CONFIRMATION_IN_FLIGHT',
      error: 'Confirmation is already being executed.',
    }
  }
  // wave-3 (Lane A) — closes cell (c): a second, independently-confirmed
  // plan (different confirmationId) whose declared document paths overlap
  // a plan that is currently applying fails closed instead of silently
  // clobbering it. confirmationsInFlight (above) only protects the SAME
  // confirmationId from re-entry; this protects two DIFFERENT plans racing
  // the same document.
  const touchedDocumentPaths = changedPaths(item.plan)
  const lockedByAnotherPlan = touchedDocumentPaths.find((path) => {
    const holder = documentPathsInFlight.get(path)
    return holder !== undefined && holder !== confirmationId
  })
  if (lockedByAnotherPlan) {
    return {
      status: 'failed',
      code: 'CONFIRMATION_DOCUMENT_LOCKED',
      error: `Another confirmed operation is already applying to ${lockedByAnotherPlan}. Wait for it to finish, then confirm again.`,
    }
  }
  confirmationsInFlight.add(confirmationId)
  for (const path of touchedDocumentPaths) documentPathsInFlight.set(path, confirmationId)
  try {
    const durableJournal = item.journalMode === 'durable'
    if (durableJournal) {
      const journalMode = await createStudioOrchestrationJournal(item.orchestrationPlan)
      if (journalMode !== 'durable') {
        return {
          status: 'failed',
          code: 'DURABLE_JOURNAL_REQUIRED',
          error: 'Authenticated orchestration journal access is required to confirm this durable run.',
        }
      }
    }
    recordConfirmationLifecycle(item.scope, {
      confirmationId,
      status: 'executing',
    })
    if (!durableJournal) {
      pending.delete(confirmationId)
      persistPendingCapabilities(item.scope)
      notifyGatewayListeners()
    } else {
      notifyGatewayListeners()
    }
    const result = await executeConfirmedPlan(
      item.plan,
      bus,
      item.scope,
      item.orchestrationPlan as CoogoOrchestrationPlan,
      legacyWriterEvents(item.events),
      durableJournal,
      {
        required: true,
        source,
        confirmationId,
        approvedAt: new Date().toISOString(),
      },
    )
    recordConfirmationLifecycle(item.scope, {
      confirmationId,
      status: result.status === 'executed' ? 'executed' : 'failed',
      ...(result.status === 'executed'
        ? { executionId: result.execution.executionId }
        : {
            error: result.error,
            ...(result.code ? { failureCode: result.code } : {}),
            ...(result.execution ? { executionId: result.execution.executionId } : {}),
          }),
    })
    pending.delete(confirmationId)
    persistPendingCapabilities(item.scope)
    notifyGatewayListeners()
    return result
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    recordConfirmationLifecycle(item.scope, {
      confirmationId,
      status: 'failed',
      error: message,
    })
    notifyGatewayListeners()
    return {
      status: 'failed',
      error: message,
    }
  } finally {
    confirmationsInFlight.delete(confirmationId)
    for (const path of touchedDocumentPaths) {
      if (documentPathsInFlight.get(path) === confirmationId) documentPathsInFlight.delete(path)
    }
  }
}

export async function confirmStudioCapability(
  confirmationId: string,
  bus: StudioMcpBus = mcpBus,
  confirmationText?: string,
): Promise<StudioCapabilityGatewayResult> {
  return confirmStudioCapabilityWithSource(
    confirmationId,
    bus,
    'studio-user-action',
    confirmationText,
  )
}

export async function cancelStudioCapability(confirmationId: string): Promise<boolean> {
  await cleanExpiredPending()
  const scope = captureStudioCapabilityScope()
  restorePendingCapabilities(scope)
  let item = pending.get(confirmationId)
  if (!item) {
    item = (await hydrateStep5PendingReference(confirmationId, scope)) ?? undefined
  }
  if (!item || !sameScope(item.scope, scope)) {
    return false
  }
  if (item.lifecycleOwner === 'executor-v2') {
    const active = activeStep5Executors.get(item.orchestrationPlan.runId)
    if (active?.cancel(item.orchestrationPlan.runId, 'studio-user-cancelled-confirmation')) {
      return true
    }
    const approvedAt = new Date().toISOString()
    const { executor } = createStep5Executor({
      item,
      bus: mcpBus,
      approvalSource: 'studio-user-action',
      approvedAt,
    })
    try {
      const result = await executor.cancelPersisted(item.orchestrationPlan, 'studio-user-cancelled-confirmation')
      if (result.status !== 'cancelled') return false
      const execution = projectStep5Execution({
        item,
        result,
        approvalSource: 'not-approved',
      })
      upsertStep5Execution(item.scope, execution)
      pending.delete(confirmationId)
      step5DurableReferences.delete(confirmationId)
      persistStep5DurableReferences(item.scope)
      recordConfirmationLifecycle(item.scope, {
        confirmationId,
        status: 'cancelled',
        executionId: execution.executionId,
      })
      notifyGatewayListeners()
      return true
    } catch {
      return false
    }
  }
  if (confirmationsInFlight.has(confirmationId)) return false
  confirmationsInFlight.add(confirmationId)
  try {
    if (item.journalMode === 'durable') {
      const journalMode = await cancelStudioOrchestrationJournal({
        projectId: item.orchestrationPlan.projectId,
        runId: item.orchestrationPlan.runId,
        reason: 'studio-user-cancelled-confirmation',
      })
      if (journalMode !== 'durable') return false
    }
    const cancelled = pending.delete(confirmationId)
    if (cancelled) {
      recordConfirmationLifecycle(item.scope, {
        confirmationId,
        status: 'cancelled',
      })
      persistPendingCapabilities(item.scope)
      notifyGatewayListeners()
    }
    return cancelled
  } finally {
    confirmationsInFlight.delete(confirmationId)
  }
}

export async function readStudioCapabilityOrchestrationRun(input: { projectId: string; runId: string }) {
  return readStudioOrchestrationJournal(input)
}

export function listPendingStudioCapabilities(): StudioPendingCapability[] {
  void cleanExpiredPending()
  const scope = captureStudioCapabilityScope()
  restorePendingCapabilities(scope)
  hydrateStep5PendingReferencesForList(scope)
  return [...pending.values()]
    .filter((item) => sameScope(item.scope, scope))
    .map((item) => ({
      confirmationId: item.confirmationId,
      planId: item.plan.planId,
      label: item.plan.label,
      tools: item.plan.commands.map((command) => command.tool),
      changedPaths: changedPaths(item.plan),
      projectId: item.scope.projectId,
      createdAt: new Date(item.createdAt).toISOString(),
      expiresAt: new Date(item.expiresAt).toISOString(),
      impact: structuredClone(item.impact),
      orchestration:
        item.lifecycleOwner === 'executor-v2'
          ? step5OrchestrationReference(item.orchestrationPlan as CoogoOrchestrationPlanV2)
          : studioOrchestrationReference(item.orchestrationPlan as CoogoOrchestrationPlan),
    }))
}

function pendingCapabilityResult(
  item: PendingCapabilityPlan,
): Extract<StudioCapabilityGatewayResult, { status: 'confirmation-required' }> {
  return {
    status: 'confirmation-required',
    confirmationId: item.confirmationId,
    planId: item.plan.planId,
    label: item.plan.label,
    tools: item.plan.commands.map((command) => command.tool),
    changedPaths: changedPaths(item.plan),
    projectId: item.scope.projectId,
    expiresAt: new Date(item.expiresAt).toISOString(),
    impact: structuredClone(item.impact),
    orchestration:
      item.lifecycleOwner === 'executor-v2'
        ? step5OrchestrationReference(item.orchestrationPlan as CoogoOrchestrationPlanV2)
        : studioOrchestrationReference(item.orchestrationPlan as CoogoOrchestrationPlan),
  }
}

export function listStudioCapabilityExecutions(): StudioCapabilityExecution[] {
  return getScopedExecutions(captureStudioCapabilityScope()).map((execution) =>
    structuredClone(execution),
  )
}

function durableStateReadback(input: {
  projectId: string
  capturedAt: string
  fingerprint?: unknown
}): StudioCapabilityStateReadback {
  return {
    capturedAt: input.capturedAt,
    projectId: input.projectId,
    activePageId: null,
    pageCount: 0,
    blockCount: 0,
    flowCount: 0,
    sourceWorkspaceRevision: null,
    documentWorkspaceRevision: null,
    fingerprint: typeof input.fingerprint === 'string' ? input.fingerprint : 'Unavailable in durable event record',
  }
}

function recoverStudioCapabilityExecution(
  readback: StudioOrchestrationRunReadback,
  scope: StudioCapabilityScope,
): StudioCapabilityExecution {
  const terminalEvent = [...readback.events]
    .reverse()
    .find((event) => event.type === 'plan.completed' || event.type === 'plan.failed' || event.type === 'plan.cancelled')
  const terminalData = terminalEvent?.data ?? {}
  const suspended = readback.events.some((event) => event.type === 'plan.suspended')
  const resumed = [...readback.events].reverse().find((event) => event.type === 'plan.resumed')
  const approvalSource = resumed?.data?.approvalSource
  const status: StudioCapabilityExecution['status'] =
    readback.run.status === 'completed'
      ? 'executed'
      : readback.run.status === 'failed'
        ? 'failed'
        : readback.run.status === 'suspended'
          ? 'suspended'
          : readback.run.status === 'cancelled'
            ? 'cancelled'
            : 'running'
  const tools = readback.plan.nodes.map((node) => node.operation?.name ?? node.label ?? node.id)
  const changedPaths = readback.plan.nodes.flatMap((node) => {
    const paths = node.input?.expectedChangedPaths
    return Array.isArray(paths) ? paths.filter((path): path is string => typeof path === 'string') : []
  })
  const results: StudioCapabilityExecution['results'] = readback.plan.nodes.map((node, index) => {
    const latest = [...readback.events].reverse().find((event) => event.nodeId === node.id)
    const readbackStatus = latest?.data?.readbackStatus
    return {
      tool: tools[index],
      success: latest?.type === 'node.completed',
      ...(latest?.type === 'node.failed'
        ? {
            error: String(latest.data?.error ?? 'The durable run recorded a failed node.'),
          }
        : {}),
      ...(readbackStatus === 'success'
        ? {
            readback: {
              status: 'success' as const,
              retained: false,
            },
          }
        : readbackStatus === 'failed'
          ? {
              readback: {
                status: 'failed' as const,
                error: 'The durable run recorded a failed readback.',
              },
            }
          : {}),
    }
  })
  const source: StudioCapabilityExecution['source'] =
    readback.plan.trigger === 'webmcp'
      ? 'webmcp'
      : readback.plan.trigger === 'studio-ui'
        ? 'studio-ui'
        : 'chat-operation'
  const rollbackSucceeded = terminalData.rollbackSucceeded
  return {
    executionId: `durable:${readback.plan.runId}`,
    planId: readback.plan.planId,
    label: readback.plan.label ?? readback.plan.planId,
    source,
    projectId: readback.plan.projectId,
    sessionId: scope.sessionId,
    status,
    tools,
    changedPaths,
    before: durableStateReadback({
      projectId: readback.plan.projectId,
      capturedAt: readback.run.createdAt,
      fingerprint: terminalData.beforeFingerprint,
    }),
    after: durableStateReadback({
      projectId: readback.plan.projectId,
      capturedAt: readback.run.updatedAt,
      fingerprint: terminalData.afterFingerprint,
    }),
    results,
    undoAvailable: false,
    ...(typeof rollbackSucceeded === 'boolean'
      ? {
          rollback: {
            attempted: terminalData.rollbackAttempted === true,
            succeeded: rollbackSucceeded,
            ...(!rollbackSucceeded
              ? {
                  error: 'The durable run did not verify restoration of the pre-run state.',
                }
              : {}),
          },
        }
      : {}),
    approval: {
      required: suspended,
      source:
        approvalSource === 'webmcp-user-approval'
          ? 'webmcp-user-approval'
          : approvalSource === 'studio-user-action'
            ? 'studio-user-action'
            : status === 'suspended'
              ? 'awaiting-user-approval'
              : suspended
                ? 'not-approved'
                : 'not-required',
    },
    createdAt: readback.run.createdAt,
    ...(status === 'failed' || status === 'cancelled'
      ? {
          error: `The durable run is ${readback.run.status}. Review its recorded steps before retrying.`,
        }
      : {}),
    orchestration: studioOrchestrationReference(readback.plan),
    events: readback.events,
  }
}

function isTerminalCapabilityExecution(execution: StudioCapabilityExecution): boolean {
  return (
    execution.status === 'executed' ||
    execution.status === 'failed' ||
    execution.status === 'cancelled' ||
    execution.status === 'undone'
  )
}

export async function hydrateStudioCapabilityExecutionsFromDurableJournal(): Promise<void> {
  const scope = captureStudioCapabilityScope()
  if (!scope.projectId || !hasAuthenticatedDurableJournalScope(scope)) return
  const executions = getScopedExecutions(scope)
  const hasRefreshableDurableExecution = executions.some(
    (execution) => execution.executionId.startsWith('durable:') && !isTerminalCapabilityExecution(execution),
  )
  if (durableHydratedScopes.has(scope.key) && !hasRefreshableDurableExecution) {
    return
  }
  const references = await listStudioOrchestrationJournals(scope.projectId)
  if (references === null) return
  const readbacks = await Promise.all(
    references.slice(0, 20).map(async (reference) => {
      try {
        return await readStudioOrchestrationJournal({
          projectId: reference.projectId,
          runId: reference.runId,
        })
      } catch {
        return null
      }
    }),
  )
  const hydrationComplete = readbacks.every((readback) => readback !== null)
  let changed = false
  for (const readback of readbacks) {
    if (!readback || readback.plan.projectId !== scope.projectId) continue
    const existingIndex = executions.findIndex((execution) => execution.orchestration.runId === readback.plan.runId)
    if (existingIndex >= 0 && isTerminalCapabilityExecution(executions[existingIndex])) {
      continue
    }
    const recovered = recoverStudioCapabilityExecution(readback, scope)
    if (existingIndex >= 0) executions[existingIndex] = recovered
    else executions.push(recovered)
    changed = true
  }
  const hasNonterminalReadback = readbacks.some(
    (readback) =>
      readback !== null && !isTerminalCapabilityExecution(recoverStudioCapabilityExecution(readback, scope)),
  )
  if (hydrationComplete && !hasNonterminalReadback) {
    durableHydratedScopes.add(scope.key)
  } else {
    durableHydratedScopes.delete(scope.key)
  }
  if (!changed) return
  executions.sort((left, right) => left.createdAt.localeCompare(right.createdAt))
  if (executions.length > MAX_EXECUTIONS) {
    executions.splice(0, executions.length - MAX_EXECUTIONS)
  }
  persistExecutions(scope, executions)
  notifyGatewayListeners()
}

export function describeStudioCapabilities(
  input: {
    operationId?: string
    surface?: string
  } = {},
) {
  const studio = useStudioStore.getState()
  const requestedOperation = input.operationId
    ? getStudioChatOperationContract(input.operationId)
    : undefined
  const activeSurface = input.surface ?? studio.activeRailItem
  const operations = listStudioChatOperationContracts().filter(
    (operation) =>
      operation.surface.railItemId === activeSurface || operation.id === requestedOperation?.id,
  )
  const executions = listStudioCapabilityExecutions()
  const impactDescriptor = (operationId: string) => {
    const contract = getStudioOperationImpactContract(operationId)
    return contract
      ? {
          required: true,
          surface: contract.surface,
          surfaces: contract.affectedSurfaces ?? [contract.surface],
          authority: contract.authority,
          staleConfirmationRejected: true,
        }
      : null
  }
  return {
    activeSurface,
    projectLoaded: Boolean(studio.currentProjectId || studio.currentProject),
    selectedOperation: requestedOperation
      ? {
          id: requestedOperation.id,
          label: requestedOperation.label,
          availability: requestedOperation.availability,
          targetKinds: requestedOperation.targetKinds,
          preconditions: requestedOperation.preconditions,
          confirmation: requestedOperation.confirmation,
          readback: requestedOperation.readback,
          undo: requestedOperation.undo,
          guidance: requestedOperation.guidance,
          exactImpact: impactDescriptor(requestedOperation.id),
        }
      : null,
    operations: operations.map((operation) => ({
      id: operation.id,
      label: operation.label,
      availability: operation.availability,
      readOnly: operation.readOnly,
      targetKinds: operation.targetKinds,
      preconditions: operation.preconditions,
      confirmation: operation.confirmation,
      exactImpact: impactDescriptor(operation.id),
      example: operation.examples[0] ?? operation.description,
    })),
    pending: listPendingStudioCapabilities(),
    latestExecution: executions[executions.length - 1] ?? null,
    boundaries: {
      missingTarget: 'Ask the user to select or name a target.',
      missingInput:
        'Ask for the required value or source; do not infer credentials or source facts.',
      unavailable: 'Explain the blocker and do not claim completion.',
      mutation: 'Use cogo.studio.capability.executeOperation and wait for confirmation.',
    },
  }
}

export async function undoStudioCapabilityExecution(
  executionId: string,
  bus: StudioMcpBus = mcpBus,
): Promise<{
  undone: boolean
  execution?: StudioCapabilityExecution
  error?: string
  /**
   * Machine-readable reason for the three failure branches below
   * (display-boundary repair, final-review I5, wave-1 fix-round-2) — the
   * display layer resolves this through locale copy instead of showing
   * `error` (an English diagnostic literal) as the primary line. `error`
   * still carries the raw English text as secondary/diagnostic detail.
   * Left unset for the 4th non-throwing failure below (`!result.success`,
   * a few lines down) — that `error` comes from the MCP bus/tool layer,
   * not one of these three fixed literals, and is out of this repair's
   * scope.
   */
  reason?: 'executionNotFound' | 'notLatestUndoable' | 'undoStackChanged'
}> {
  const scope = captureStudioCapabilityScope()
  const executions = getScopedExecutions(scope)
  const execution = executions.find((item) => item.executionId === executionId)
  if (!execution) {
    return {
      undone: false,
      error: 'Capability execution not found.',
      reason: 'executionNotFound',
    }
  }
  const latestUndoable = [...executions]
    .reverse()
    .find(
      (item) =>
        item.undoAvailable &&
        (item.status === 'executed' ||
          (item.status === 'failed' && item.rollback?.succeeded === false)),
    )
  if (latestUndoable?.executionId !== executionId) {
    return {
      undone: false,
      error: 'Only the latest undoable capability execution can be reverted.',
      reason: 'notLatestUndoable',
    }
  }
  if (getUndoState().topCapabilityPlanId !== execution.planId) {
    return {
      undone: false,
      error:
        'The global undo stack changed after this capability execution. Undo the intervening action first.',
      reason: 'undoStackChanged',
    }
  }
  const result = await bus.execute({
    tool: 'cogo.studio.undo',
    params: execution.status === 'failed' ? { _discardRedo: true } : {},
  })
  if (!result.success) return { undone: false, error: result.error }
  execution.status = 'undone'
  execution.after = captureStudioCapabilityState()
  persistExecutions(scope, executions)
  notifyGatewayListeners()
  return { undone: true, execution: structuredClone(execution) }
}

export function resetStudioCapabilityGatewayForTests(
  options: { preserveStorage?: boolean } = {},
): void {
  pending.clear()
  confirmationsInFlight.clear()
  documentPathsInFlight.clear()
  confirmationLifecycles.clear()
  activeStep5Executors.clear()
  step5DurableReferences.clear()
  step5PendingHydrations.clear()
  durableHydratedScopes.clear()
  executionsByScope.clear()
  restoredPendingScopes.clear()
  agUiOperationRequests.clear()
  if (!options.preserveStorage) inMemorySessionId = null
  legacyExecutionStorageCleared = false
  gatewayRevision = 0
  if (options.preserveStorage) return
  const storage = getSessionStorage()
  try {
    for (let index = (storage?.length ?? 0) - 1; index >= 0; index -= 1) {
      const key = storage?.key(index)
      if (
        key === CAPABILITY_SESSION_STORAGE_KEY ||
        key?.startsWith(CAPABILITY_EXECUTION_STORAGE_PREFIX) ||
        key?.startsWith(CAPABILITY_PENDING_STORAGE_PREFIX) ||
        key?.startsWith(CAPABILITY_STEP5_REFERENCE_STORAGE_PREFIX)
      ) {
        storage?.removeItem(key)
      }
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(LEGACY_EXECUTION_STORAGE_KEY)
    }
  } catch {
    // Test cleanup remains best-effort when storage is unavailable.
  }
}

interface OperationInputError {
  instancePath: string
  schemaPath: string
  keyword: string
  params: Record<string, unknown>
  message?: string
}

function validateOperationSchema(
  schema: StudioJsonSchema,
  value: unknown,
  instancePath = '',
  schemaPath = '#',
): OperationInputError[] {
  const errors: OperationInputError[] = []
  const addError = (keyword: string, params: Record<string, unknown>, message: string) => {
    errors.push({
      instancePath,
      schemaPath: `${schemaPath}/${keyword}`,
      keyword,
      params,
      message,
    })
  }

  if (schema.anyOf?.length) {
    const matches = schema.anyOf.some(
      (candidate, index) =>
        validateOperationSchema(candidate, value, instancePath, `${schemaPath}/anyOf/${index}`)
          .length === 0,
    )
    if (!matches) {
      addError('anyOf', {}, 'must match a supported input shape')
      return errors
    }
  }

  if (schema.const !== undefined && !Object.is(value, schema.const)) {
    addError('const', { allowedValue: schema.const }, 'must equal the allowed value')
  }
  if (schema.enum && !schema.enum.some((entry) => Object.is(entry, value))) {
    addError('enum', { allowedValues: schema.enum }, 'must be an allowed value')
  }

  if (schema.type) {
    const matchesType =
      schema.type === 'null'
        ? value === null
        : schema.type === 'array'
          ? Array.isArray(value)
          : schema.type === 'object'
            ? Boolean(value) && typeof value === 'object' && !Array.isArray(value)
            : schema.type === 'integer'
              ? Number.isInteger(value)
              : typeof value === schema.type
    if (!matchesType) {
      addError('type', { type: schema.type }, `must be ${schema.type}`)
      return errors
    }
  }

  const hasObjectKeywords =
    schema.type === 'object' ||
    Boolean(schema.properties) ||
    Boolean(schema.required?.length) ||
    schema.minProperties !== undefined ||
    schema.additionalProperties !== undefined
  if (hasObjectKeywords && value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    const properties = schema.properties ?? {}
    for (const required of schema.required ?? []) {
      if (!Object.prototype.hasOwnProperty.call(record, required)) {
        errors.push({
          instancePath,
          schemaPath: `${schemaPath}/required`,
          keyword: 'required',
          params: { missingProperty: required },
          message: 'is required',
        })
      }
    }
    if (
      typeof schema.minProperties === 'number' &&
      Object.keys(record).length < schema.minProperties
    ) {
      addError(
        'minProperties',
        { limit: schema.minProperties },
        `must have at least ${schema.minProperties} properties`,
      )
    }
    for (const [key, nestedValue] of Object.entries(record)) {
      const nestedSchema = properties[key]
      if (nestedSchema) {
        errors.push(
          ...validateOperationSchema(
            nestedSchema,
            nestedValue,
            `${instancePath}/${key}`,
            `${schemaPath}/properties/${key}`,
          ),
        )
        continue
      }
      if (schema.additionalProperties === false) {
        errors.push({
          instancePath,
          schemaPath: `${schemaPath}/additionalProperties`,
          keyword: 'additionalProperties',
          params: { additionalProperty: key },
          message: 'must not include additional properties',
        })
      } else if (schema.additionalProperties && typeof schema.additionalProperties === 'object') {
        errors.push(
          ...validateOperationSchema(
            schema.additionalProperties,
            nestedValue,
            `${instancePath}/${key}`,
            `${schemaPath}/additionalProperties`,
          ),
        )
      }
    }
  }

  if (schema.type === 'array' && Array.isArray(value)) {
    if (typeof schema.minItems === 'number' && value.length < schema.minItems) {
      addError(
        'minItems',
        { limit: schema.minItems },
        `must have at least ${schema.minItems} items`,
      )
    }
    if (schema.items) {
      value.forEach((item, index) => {
        errors.push(
          ...validateOperationSchema(
            schema.items as StudioJsonSchema,
            item,
            `${instancePath}/${index}`,
            `${schemaPath}/items`,
          ),
        )
      })
    }
  }

  if (schema.type === 'string' && typeof value === 'string') {
    if (typeof schema.minLength === 'number' && value.length < schema.minLength) {
      addError(
        'minLength',
        { limit: schema.minLength },
        `must have at least ${schema.minLength} characters`,
      )
    }
  }

  if ((schema.type === 'number' || schema.type === 'integer') && typeof value === 'number') {
    if (typeof schema.minimum === 'number' && value < schema.minimum) {
      addError('minimum', { limit: schema.minimum }, `must be at least ${schema.minimum}`)
    }
    if (typeof schema.maximum === 'number' && value > schema.maximum) {
      addError('maximum', { limit: schema.maximum }, `must be at most ${schema.maximum}`)
    }
  }

  return errors
}

function operationInputErrorMessage(error: OperationInputError): string {
  const path = error.instancePath ? `input${error.instancePath.replaceAll('/', '.')}` : 'input'
  if (error.keyword === 'required') {
    const property = String(error.params.missingProperty ?? 'property')
    return `${path}.${property} is required`
  }
  if (error.keyword === 'additionalProperties') {
    const property = String(error.params.additionalProperty ?? 'property')
    return `${path}.${property} is not allowed`
  }
  if (
    error.keyword === 'minLength' ||
    error.keyword === 'minItems' ||
    error.keyword === 'minProperties'
  ) {
    return `${path} must not be empty`
  }
  if (error.keyword === 'enum') {
    const allowed = Array.isArray(error.params.allowedValues)
      ? error.params.allowedValues.map(String).join(', ')
      : 'an allowed value'
    return `${path} must be one of: ${allowed}`
  }
  if (error.keyword === 'type') {
    return `${path} must be ${String(error.params.type ?? 'the expected type')}`
  }
  if (error.keyword === 'anyOf') {
    return `${path} must include at least one supported change field`
  }
  return `${path} ${error.message ?? 'is invalid'}`
}

function validateStudioOperationInput(
  operation: NonNullable<ReturnType<typeof getStudioChatOperationContract>>,
  params: Record<string, unknown>,
): string | null {
  const errors = validateOperationSchema(operation.inputSchema, params)
  if (errors.length === 0) return null
  const hasAnyOfFailure = errors.some((error) => error.keyword === 'anyOf')
  const messages = errors
    .filter(
      (error) =>
        !hasAnyOfFailure || error.keyword === 'anyOf' || !error.schemaPath.startsWith('#/anyOf/'),
    )
    .map(operationInputErrorMessage)
  const details = [...new Set(messages)].join('; ')
  return `Invalid input for Studio operation ${operation.id}: ${
    details || 'input does not match the operation contract'
  }.`
}

async function executeStudioChatOperationOnce(
  operationId: string,
  params: Record<string, unknown>,
  options: { bus?: StudioMcpBus; orchestration?: { runId: string; toolCallId: string } } = {},
): Promise<StudioCapabilityGatewayResult> {
  const operation = getStudioChatOperationContract(operationId)
  if (!operation) {
    return {
      status: 'failed',
      error: `Unknown Studio operation: ${operationId}`,
    }
  }
  if (operation.status !== 'ready' || !operation.webMcpToolName) {
    return {
      status: 'failed',
      error:
        operation.guidance ??
        `Studio operation is not executable: ${operation.id} (${operation.status}).`,
    }
  }
  if (!operation.webMcpToolName.startsWith('cogo.studio.')) {
    return {
      status: 'failed',
      error: `Studio operation uses an external readback tool and cannot mutate through this gateway: ${operation.webMcpToolName}`,
    }
  }
  const inputError = validateStudioOperationInput(operation, params)
  if (inputError) {
    return { status: 'failed', error: inputError }
  }
  if (operation.id === 'workspace.semantic-sync') {
    try {
      const callHash = options.orchestration
        ? stableHash(`${options.orchestration.runId}:${options.orchestration.toolCallId}`)
        : crypto.randomUUID()
      const plan = buildStudioSemanticSyncPlan({
        params,
        planId: `operation:${operation.id}:${callHash}`,
        ...(options.orchestration
          ? {
              runId: `ag:${stableHash(options.orchestration.runId)}:${callHash}`,
              parentRunId: options.orchestration.runId,
            }
          : {}),
      })
      return executeStudioCapabilityPlan(plan, { bus: options.bus ?? mcpBus })
    } catch (error) {
      return {
        status: 'failed',
        error: `Semantic sync preflight failed: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }
  let designMutationPreview:
    | ReturnType<typeof buildOpenPencilCogoDesignMutationImpactPreview>
    | undefined
  if (operation.id === 'source.figma.apply_design_mutation') {
    const state = useStudioStore.getState()
    if (!state.currentProjectId || !state.currentProject || !state.openPencilSourceWorkspace) {
      return {
        status: 'failed',
        error:
          'Design mutation confirmation requires the active Coogo project and editable OpenPencil workspace.',
      }
    }
    try {
      designMutationPreview = buildOpenPencilCogoDesignMutationImpactPreview({
        proposal: params.proposal,
        workspace: state.openPencilSourceWorkspace,
        currentProjectId: state.currentProjectId,
        currentProject: state.currentProject,
      })
    } catch (error) {
      return {
        status: 'failed',
        error: `Design mutation confirmation preflight failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
      }
    }
  }
  let coreSurfaceImpact: CoreSurfaceDestructiveImpact | null
  try {
    coreSurfaceImpact = buildCoreSurfaceDestructiveImpact(operation.id, params)
  } catch (error) {
    return {
      status: 'failed',
      error: `Core surface impact preflight failed: ${error instanceof Error ? error.message : String(error)}`,
    }
  }
  let projectMutationImpact: StudioProjectMutationImpact | null
  try {
    projectMutationImpact = buildProjectMutationImpact(operation.id, params)
  } catch (error) {
    return {
      status: 'failed',
      error: `Project mutation impact preflight failed: ${error instanceof Error ? error.message : String(error)}`,
    }
  }
  let declaredOperationImpact: StudioProjectMutationImpact | null
  try {
    declaredOperationImpact =
      operation.readOnly === true
        ? null
        : buildDeclaredStudioOperationImpact(operation.id, params)
  } catch (error) {
    return {
      status: 'failed',
      error: `Exact operation impact preflight failed: ${error instanceof Error ? error.message : String(error)}`,
    }
  }
  const mutationImpact = designMutationPreview
    ? {
        expectedChangedPaths: designMutationPreview.changedPaths,
        exactChanges: designMutationPreview.changes.map((change) => ({
          ...change,
          label: pathLabel(change.path),
        })),
      }
      : coreSurfaceImpact
        ? {
            expectedChangedPaths: coreSurfaceImpact.expectedChangedPaths,
            exactChanges: coreSurfaceImpact.exactChanges,
          }
        : (projectMutationImpact ?? declaredOperationImpact)
  const orchestrationCallHash = options.orchestration
    ? stableHash(`${options.orchestration.runId}:${options.orchestration.toolCallId}`)
    : null
  return executeStudioCapabilityPlan(
    {
      planId: options.orchestration
        ? `operation:${operation.id}:${orchestrationCallHash}`
        : `operation:${operation.id}:${crypto.randomUUID()}`,
      ...(options.orchestration
        ? {
            runId: `ag:${stableHash(options.orchestration.runId)}:${orchestrationCallHash}`,
            parentRunId: options.orchestration.runId,
          }
        : {}),
      label: operation.label,
      intent: operation.description,
      source: 'chat-operation',
      commands: [
        {
          tool: operation.webMcpToolName,
          params,
          operationId: operation.id,
          ...(options.orchestration ? { sourceCallId: options.orchestration.toolCallId } : {}),
          ...(mutationImpact ?? {}),
          requiresConfirmation:
            operation.readOnly === true ? false : operation.requiresConfirmation !== false,
        },
        ...(coreSurfaceImpact
          ? [
              {
                tool: CORE_SURFACE_READBACK_TOOL,
                params: coreSurfaceImpact.readbackParams,
                requiresConfirmation: false,
              },
            ]
          : []),
      ],
    },
    options,
  )
}

async function reconcileAgUiOperationReplay(input: {
  operationId: string
  runId: string
  toolCallId: string
}): Promise<StudioCapabilityGatewayResult | null> {
  const scope = captureStudioCapabilityScope()
  const callHash = stableHash(`${input.runId}:${input.toolCallId}`)
  const planId = `operation:${input.operationId}:${callHash}`
  const durableRunId = `ag:${stableHash(input.runId)}:${callHash}`
  restorePendingCapabilities(scope)
  const storedPending = [...pending.values()].find(
    (item) =>
      sameScope(item.scope, scope) && item.plan.planId === planId && item.orchestrationPlan.runId === durableRunId,
  )
  if (storedPending) return pendingCapabilityResult(storedPending)

  const storedExecution = getScopedExecutions(scope).find(
    (execution) => execution.planId === planId && execution.orchestration.runId === durableRunId,
  )
  if (storedExecution) {
    const execution = structuredClone(storedExecution)
    return execution.status === 'executed' || execution.status === 'undone'
      ? { status: 'executed', execution }
      : {
          status: 'failed',
          execution,
          error: execution.error ?? `The replayed AG-UI tool call already has status ${execution.status}.`,
        }
  }

  if (!scope.projectId) return null
  let readback: StudioOrchestrationRunReadback | null
  try {
    readback = await readStudioOrchestrationJournal({
      projectId: scope.projectId,
      runId: durableRunId,
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    if (message.includes('(404)')) return null
    return {
      status: 'failed',
      error: `Studio could not verify whether this AG-UI tool call already ran: ${message}`,
    }
  }
  if (!readback) return null
  if (readback.plan.planId !== planId || readback.plan.parentRunId !== input.runId) {
    return {
      status: 'failed',
      error: 'The durable AG-UI run identity conflicts with this tool call. Studio refused to replay it.',
    }
  }
  const recovered = recoverStudioCapabilityExecution(readback, scope)
  appendExecution(scope, recovered)
  return recovered.status === 'executed'
    ? { status: 'executed', execution: recovered }
    : {
        status: 'failed',
        execution: recovered,
        error:
          recovered.status === 'suspended'
            ? 'This AG-UI tool call is already waiting for approval in another Studio session. Cancel it there or start a new request.'
            : `This AG-UI tool call already has durable status ${recovered.status}.`,
      }
}

function isTerminalAgUiOperationResult(result: StudioCapabilityGatewayResult): boolean {
  if (result.status === 'executed') return true
  return result.status === 'failed' && result.execution !== undefined && isTerminalCapabilityExecution(result.execution)
}

export async function executeStudioChatOperation(
  operationId: string,
  params: Record<string, unknown>,
  options: {
    bus?: StudioMcpBus
    orchestration?: { runId: string; toolCallId: string }
  } = {},
): Promise<StudioCapabilityGatewayResult> {
  if (!options.orchestration) {
    return executeStudioChatOperationOnce(operationId, params, options)
  }
  const runId = options.orchestration.runId.trim()
  const toolCallId = options.orchestration.toolCallId.trim()
  if (!runId || !toolCallId) {
    return {
      status: 'failed',
      error: 'AG-UI orchestration requires non-empty runId and toolCallId values.',
    }
  }
  const scope = captureStudioCapabilityScope()
  const replayKey = JSON.stringify([scope.key, operationId, runId, toolCallId])
  const fingerprint = stableHash(JSON.stringify([operationId, params]))
  const existing = agUiOperationRequests.get(replayKey)
  if (existing) {
    if (existing.fingerprint !== fingerprint) {
      return {
        status: 'failed',
        error: 'AG-UI reused one toolCallId with different operation input. Studio refused the conflicting replay.',
      }
    }
    if (existing.promise) return existing.promise
  }
  const normalizedOptions = {
    ...options,
    orchestration: { runId, toolCallId },
  }
  const promise: Promise<StudioCapabilityGatewayResult> = (async () => {
    try {
      const replay = await reconcileAgUiOperationReplay({
        operationId,
        runId,
        toolCallId,
      })
      return replay ?? executeStudioChatOperationOnce(operationId, params, normalizedOptions)
    } catch (error) {
      return {
        status: 'failed',
        error: `AG-UI tool execution failed safely: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  })()
  if (agUiOperationRequests.size >= MAX_AG_UI_OPERATION_REQUESTS) {
    const oldestSettled = [...agUiOperationRequests].find(
      ([, request]) => request.settled,
    )
    if (oldestSettled) agUiOperationRequests.delete(oldestSettled[0])
  }
  const request = { fingerprint, promise, settled: false }
  agUiOperationRequests.set(replayKey, request)
  void promise.then(
    (result) => {
      if (agUiOperationRequests.get(replayKey) !== request) return
      request.settled = true
      if (!isTerminalAgUiOperationResult(result)) {
        agUiOperationRequests.set(replayKey, { fingerprint, settled: true })
      }
    },
    () => {
      if (agUiOperationRequests.get(replayKey) === request) {
        agUiOperationRequests.set(replayKey, { fingerprint, settled: true })
      }
    },
  )
  return promise
}

export function registerStudioCapabilityGatewayTools(bus: StudioMcpBus): void {
  const protectedCreatorMutationTools = new Set(
    [
      ...listStudioChatOperationContracts()
      .filter(
        (operation) =>
          operation.status === 'ready' &&
          operation.readOnly !== true &&
          operation.webMcpToolName?.startsWith('cogo.studio.') &&
          operation.webMcpToolName !== 'cogo.studio.capability.executeOperation' &&
          !HANDLER_ENFORCED_APPROVAL_TOOLS.has(operation.webMcpToolName),
      )
      .map((operation) => operation.webMcpToolName as string),
      'cogo.studio.assistant.generateApplication',
    ]
  )
  bus.requireCapabilityApproval(protectedCreatorMutationTools, (command) => {
    consumeStudioCapabilityApprovalGrant({
      token: command.params._capabilityApprovalToken,
      tool: command.tool,
      params: command.params,
      projectId: useStudioStore.getState().currentProjectId,
      failureScope: 'studio',
    })
  })

  bus.registerTool('capability.readbackCoreSurface', {
    description:
      'Read Page, ActionFlow, Data, or Document target presence. Server-backed projects flush both save lanes and compare a detached authoritative project GET without replacing the local draft; local projects remain working-copy-only.',
    metadata: {
      readOnly: false,
      tags: ['chat', 'webmcp', 'readback', 'persistence'],
    },
    inputSchema: {
      type: 'object',
      required: ['surface', 'expectation'],
      properties: {
        surface: {
          type: 'string',
          enum: ['page', 'actionflow', 'data', 'document'],
        },
        expectation: { type: 'string', enum: ['present', 'absent'] },
        pageId: { type: 'string' },
        expectedActivePageId: { type: 'string' },
        expectedEditorDefaultRoute: { type: 'string' },
        expectedProjectDefaultRoute: { type: 'string' },
        expectedEditorPageIds: { type: 'array', items: { type: 'string' } },
        expectedProjectPageIds: { type: 'array', items: { type: 'string' } },
        flowId: { type: 'string' },
        flowName: { type: 'string' },
        flowKey: { type: 'string' },
        blockName: { type: 'string' },
        bindingPaths: { type: 'array', items: { type: 'string' } },
        projectMirrorKeys: { type: 'array', items: { type: 'string' } },
        sourceName: { type: 'string' },
        strict: { type: 'boolean' },
      },
    },
    handler: (payload) => readCoreSurfaceState(payload),
  })
  bus.registerTool(
    'capability.readbackExactImpact',
    createStudioExactImpactReadbackTool({
      readCurrentAuthority: (path) => {
        const value = authoritativeValueForPath(path)
        const target = classifyStudioExactImpactTarget(path)
        if (target.kind === 'page-title') {
          const page = useEditorStore
            .getState()
            .app?.pages.find((candidate) => candidate.id === target.pageId)
          return {
            presence: Boolean(
              page && Object.prototype.hasOwnProperty.call(page, 'title'),
            ),
            value,
          }
        }
        if (target.kind === 'primary-color') {
          const project = recordOf(useStudioStore.getState().currentProject)
          const tokens = recordOf(project?.tokens)
          const colors = recordOf(tokens?.colors)
          return {
            presence: Boolean(
              colors && Object.prototype.hasOwnProperty.call(colors, 'primary'),
            ),
            value,
          }
        }
        const workspace = readCogoDocumentWorkspaceFromProject(
          useStudioStore.getState().currentProject,
        )
        const node = workspace?.graph.nodes[target.documentNodeId]
        return {
          presence: Boolean(
            node && Object.prototype.hasOwnProperty.call(node, 'text'),
          ),
          value,
        }
      },
      sanitizeValue: sanitize,
    }),
  )
  bus.registerTool('capability.describe', {
    description:
      'Describe what Studio can do on the active surface, what input is required, and which boundaries or confirmations apply.',
    metadata: {
      readOnly: true,
      tags: ['chat', 'webmcp', 'capability', 'guidance'],
    },
    inputSchema: {
      type: 'object',
      properties: {
        operationId: { type: 'string' },
        surface: { type: 'string' },
      },
    },
    handler: (payload) =>
      describeStudioCapabilities({
        ...(typeof payload.operationId === 'string' ? { operationId: payload.operationId } : {}),
        ...(typeof payload.surface === 'string' ? { surface: payload.surface } : {}),
      }),
  })
  bus.registerTool('capability.pending', {
    description: 'List capability plans waiting for explicit user confirmation.',
    metadata: { readOnly: true, tags: ['chat', 'webmcp', 'confirmation'] },
    inputSchema: { type: 'object', properties: {} },
    handler: () => ({ pending: listPendingStudioCapabilities() }),
  })
  bus.registerTool('capability.executions', {
    description: 'List capability executions with post-operation readback evidence.',
    metadata: { readOnly: true, tags: ['chat', 'webmcp', 'readback'] },
    inputSchema: { type: 'object', properties: {} },
    handler: () => ({ executions: listStudioCapabilityExecutions() }),
  })
  bus.registerTool('capability.executeOperation', {
    description:
      'Plan a Studio mutation for confirmation, or execute a read-only operation through the shared readback gateway.',
    metadata: { readOnly: true, tags: ['chat', 'webmcp', 'operation'] },
    inputSchema: {
      type: 'object',
      required: ['operationId'],
      properties: {
        operationId: { type: 'string' },
        params: { type: 'object' },
        orchestration: {
          type: 'object',
          required: ['runId', 'toolCallId'],
          properties: {
            runId: { type: 'string' },
            toolCallId: { type: 'string' },
          },
        },
      },
    },
    handler: (payload) => {
      const orchestration =
        payload.orchestration &&
        typeof payload.orchestration === 'object' &&
        typeof (payload.orchestration as Record<string, unknown>).runId === 'string' &&
        typeof (payload.orchestration as Record<string, unknown>).toolCallId === 'string'
          ? {
              runId: String((payload.orchestration as Record<string, unknown>).runId),
              toolCallId: String((payload.orchestration as Record<string, unknown>).toolCallId),
            }
          : undefined
      return executeStudioChatOperation(
        String(payload.operationId),
        payload.params && typeof payload.params === 'object'
          ? (payload.params as Record<string, unknown>)
          : {},
        { bus, ...(orchestration ? { orchestration } : {}) },
      )
    },
  })
}
